import React from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import Box from "../components/Box";
import LoadingProgress from "../components/LoadingProgress";
import ErrorMessage from "../components/ErrorMessage";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  LineChart,
  Line,
  CartesianGrid,
  Legend,
} from "recharts";
import {
  fetchPersistedSalesArqueos,
  fetchPersistedOperationalArqueos,
} from "../actions/arqueos";

// dashboard-specific styles (no reuse from other screens)
import "../styles/DashboardView.css";

function fmtCurrency(v) {
  if (v === null || v === undefined) return "—";
  return Number(v).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

// format a Date object as YYYY-MM-DD in local time (avoids UTC offset issues)
function localIso(d) {
  if (!(d instanceof Date) || isNaN(d)) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

function pct(v) {
  if (v === null || v === undefined) return "—";
  return (Number(v) * 100).toFixed(1) + "%";
}

class OperationalDashboardView extends React.Component {
  constructor(props) {
    super(props);
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth();
    // compute full current month interval
    const monthStart = new Date(year, month - 1, 1);
    const monthEndDate = new Date(year, month, 0); // last day of month
    const monthEnd = localIso(monthEndDate);

    // prepare last‑year equivalent interval
    const lyStartDate = new Date(year - 1, month - 1, 1);
    const lyEndDate = new Date(year - 1, month, 0);
    const lyStartStr = localIso(lyStartDate);
    const lyEndStr = localIso(lyEndDate);

    // comparison period defaults to last year same month

    this.state = {
      // origin / current period (committed)
      startDate: localIso(monthStart),
      endDate: monthEnd,
      // current period type (range/day/month/year)
      periodType: "range",
      // temporary inputs (editable, not applied until refresh)
      tempPeriodType: "range",
      tempStartDate: localIso(monthStart),
      tempEndDate: monthEnd,
      // comparison period (committed)
      compareStartDate: lyStartStr,
      compareEndDate: lyEndStr,
      // temporary comparison inputs
      tempCompareStartDate: lyStartStr,
      tempCompareEndDate: lyEndStr,
      loading: false,
      // filters matching the dashboard spec
      country: "",
      businessModel: "",
      // client-only vertical filter (no server fetch)
      vertical: "",
      activeOnly: false,
      siteFilter: "",
      // toggle for showing extra fetch filters
      showAdvancedFilters: false,
      // metric used for ranking top locations
      topMetric: "gmv",
      // last-year/persisted results (kept in local state)
      lySalesArqueos: null,
      lyOperationalArqueos: null,
      // flags about comparison data:
      // - did the persisted endpoint return any rows?
      lyPersistedHasData: false,
      // - did any source (persisted or generated) return rows? used for UI hints
      lyHasData: false,
      // per-donut visibility toggles (slice keys hidden by user)
      installedHidden: {
        country: [],
        category: [],
        businessModel: [],
      },
      // SVG connector lines state (computed after render)
      donutConnectors: {},
    };

    // DOM refs used to compute connector positions
    this.containerRefs = { country: null, category: null, businessModel: null };
    this.donutRefs = { country: null, category: null, businessModel: null };
    this.legendItemRefs = { country: [], category: [], businessModel: [] };
    // chart data captured during render for connector calculation
    this.latestChartData = {};
    // uncontrolled ref + debounce timer for customer filter input
    this.siteInputRef = React.createRef();
    this._siteFilterTimer = null;
  }

  componentDidMount() {
    this.load();
  }

  componentDidUpdate(prevProps, prevState) {
    // all header filters are now client-side only; data reload must be
    // initiated explicitly via the Refresh button. This lifecycle hook is
    // retained in case future non-filter state requires watching, but no
    // automatic fetch is performed.
  }

  componentWillUnmount() {
    // nothing to cleanup
  }

  // Helper: test whether a row matches a free-text site/customer filter
  rowMatchesFilter(row, q) {
    if (!row || !q) return false;
    const ql = q.toString().toLowerCase();
    const candidates = [
      row.CustomerName,
      row.Customer,
      row.CustomerId,
      row.Fullname,
      row.Site,
      row.Location,
      row.SiteName,
      row.MachineSerial,
      row.MachineId,
      row.Serial,
    ];
    return candidates.some((c) => {
      try {
        return c && c.toString().toLowerCase().includes(ql);
      } catch (e) {
        return false;
      }
    });
  }

  // helper used by the "LY" button: look at the current temporary origin
  // period and populate the comparison inputs with the same interval one
  // year earlier.  Works for all four period types.
  fillLastYear = () => {
    const { tempPeriodType, tempStartDate, tempEndDate } = this.state;
    if (!tempStartDate) return;

    const shiftYearDate = (iso, delta) => {
      const d = new Date(iso);
      if (isNaN(d)) return "";
      d.setFullYear(d.getFullYear() + delta);
      return localIso(d);
    };

    let compStart = "";
    let compEnd = "";
    switch (tempPeriodType) {
      case "range":
        compStart = shiftYearDate(tempStartDate, -1);
        compEnd = tempEndDate ? shiftYearDate(tempEndDate, -1) : "";
        break;
      case "day":
        compStart = shiftYearDate(tempStartDate, -1);
        break;
      case "month": {
        // tempStartDate is yyyy-mm
        const [y, m] = tempStartDate.split("-");
        if (y && m) {
          compStart = `${String(Number(y) - 1).padStart(4, "0")}-${m}`;
        }
        break;
      }
      case "year": {
        const y = tempStartDate;
        if (y) {
          compStart = String(Number(y) - 1);
        }
        break;
      }
    }
    this.setState({
      tempCompareStartDate: compStart,
      tempCompareEndDate: compEnd,
    });
  };

  load = async () => {
    const {
      startDate,
      endDate,
      compareStartDate,
      compareEndDate,
      country,
      businessModel,
    } = this.state;
    const { role, customerId } = this.props;
    const customerParam = role !== 'ADMIN' ? customerId : undefined;
    const makeBm = (bm) => {
      if (!bm) return undefined;
      const map = {
        revenue_share: "REVENUE SHARE",
        alquiler: "RENTAL",
        venta: "SALE",
      };
      return map[bm] || bm;
    };
    const bmParam = makeBm(businessModel);

    this.setState({ loading: true });

    try {
      // 1) fetch comparison-period persisted data first (capture into local state)
      // guard against empty strings
      // default to current month when dates are empty
      const today = new Date();
      const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
      const start = startDate ? new Date(startDate) : monthStart;
      const end = endDate ? new Date(endDate) : today;

      // if the user hasn't entered a compare range, skip loading LY data
      let lySalesAction = { response: [] };
      let lyOpAction = { response: [] };
      let persistedSalesCount = 0;
      if (compareStartDate && compareEndDate) {
        let lyStart = new Date(compareStartDate);
        let lyEnd = new Date(compareEndDate);
        const lyStartISO = localIso(lyStart);
        const lyEndISO = localIso(lyEnd);

        [lySalesAction, lyOpAction] = await Promise.all([
          this.props.fetchPersistedSalesArqueos(
            lyStartISO,
            lyEndISO,
            bmParam,
            customerParam,
            country || undefined,
          ),
          this.props.fetchPersistedOperationalArqueos(
            lyStartISO,
            lyEndISO,
            undefined,
            customerParam,
            country || undefined,
          ),
        ]);
        if (lySalesAction && Array.isArray(lySalesAction.response)) {
          persistedSalesCount = lySalesAction.response.length;
        }
      }

      const EXCLUDE_CID = "620d1664db950220fabc0a41"; // VACWAY TECH
      const excludeRow = (r) => ((r.CustomerId || r.Customer || "") + "") !== EXCLUDE_CID;
      this.setState({
        lySalesArqueos: ((lySalesAction && lySalesAction.response) || []).filter(excludeRow),
        lyOperationalArqueos: ((lyOpAction && lyOpAction.response) || []).filter(excludeRow),
        // persisted data flag (sales only)
        lyPersistedHasData: persistedSalesCount > 0,
        // mark if either sales or operational arrays contained any rows
        lyHasData:
          (lySalesAction &&
            Array.isArray(lySalesAction.response) &&
            lySalesAction.response.length > 0) ||
          (lyOpAction &&
            Array.isArray(lyOpAction.response) &&
            lyOpAction.response.length > 0),
      });

      // 2) fetch current-period persisted data (populate Redux store used elsewhere)
      await Promise.all([
        this.props.fetchPersistedSalesArqueos(
          startDate,
          endDate,
          bmParam,
          customerParam,
          country || undefined,
        ),
        this.props.fetchPersistedOperationalArqueos(
          startDate,
          endDate,
          undefined,
          customerParam,
          country || undefined,
          bmParam,
        ),
      ]);

      this.setState({ loading: false });
    } catch (err) {
      this.setState({ loading: false });
    }
  };

  // Aggregations
  aggregateSales() {
    // apply business model & country filters client-side as well
    let arr = Array.isArray(this.props.generatedSalesArqueos)
      ? this.props.generatedSalesArqueos.slice()
      : [];
    // Always exclude this CustomerId from sales arqueos
    const EXCLUDE_CUSTOMER_ID = "620d1664db950220fabc0a41"; //VACWAY TECH
    arr = arr.filter((r) => {
      const cid = (r.CustomerId || r.Customer || "") + "";
      return cid !== EXCLUDE_CUSTOMER_ID;
    });
    // filter by selected period
    const { startDate, endDate, businessModel, country } = this.state;
    if (startDate || endDate) {
      arr = arr.filter((r) => {
        const day = r.Day
          ? r.Day.slice
            ? r.Day.slice(0, 10)
            : String(r.Day).slice(0, 10)
          : null;
        if (!day) return false;
        if (startDate && day < startDate) return false;
        if (endDate && day > endDate) return false;
        return true;
      });
    }
    if (businessModel) {
      const map = {
        revenue_share: "REVENUE SHARE",
        alquiler: "RENTAL",
        venta: "SALE",
      };
      const bm = map[businessModel] || businessModel;
      arr = arr.filter((r) => r.BusinessModel === bm);
    }

    if (country && country.trim()) {
      const q = country.trim().toLowerCase();
      arr = arr.filter((r) =>
        (r.CustomerCountry || r.Country || r.SiteCountry || "")
          .toString()
          .toLowerCase()
          .includes(q),
      );
    }
    // client-only vertical filter (no server fetch)
    // Sales rows don't carry a Category field — look it up from operational rows
    // via machine serial so the filter actually finds matches.
    const { vertical } = this.state || {};
    if (vertical && vertical.trim()) {
      const q = vertical.trim().toLowerCase();
      const opRows = Array.isArray(this.props.generatedOperationalArqueos)
        ? this.props.generatedOperationalArqueos
        : [];
      const machineCategoryMap = {};
      opRows.forEach((r) => {
        const mid = (r.MachineSerial || r.MachineId || r.Serial || "") + "";
        if (mid && (r.Category || r.Vertical || r.CategoryName)) {
          machineCategoryMap[mid] = ((r.Category || r.Vertical || r.CategoryName) + "").toLowerCase();
        }
      });
      arr = arr.filter((r) => {
        // try the row's own field first (in case it's present)
        const own = ((r.Category || r.Vertical || r.CategoryName || "") + "").toLowerCase();
        if (own.includes(q)) return true;
        // fall back to operational lookup by machine serial
        const mid = (r.MachineSerial || r.MachineId || r.Serial || "") + "";
        return (machineCategoryMap[mid] || "").includes(q);
      });
    }
    return this.aggregateFromRows(arr);
  }

  aggregateFromRows(rows) {
    const { siteFilter, activeOnly } = this.state;
    const EXCLUDE_CUSTOMER_ID = "620d1664db950220fabc0a41"; // VACWAY TECH
    let arr = Array.isArray(rows) ? rows.slice() : [];
    arr = arr.filter((r) => ((r.CustomerId || r.Customer || "") + "") !== EXCLUDE_CUSTOMER_ID);

    if (siteFilter && siteFilter.trim()) {
      const q = siteFilter.trim();
      arr = arr.filter((r) => this.rowMatchesFilter(r, q));
    }

    if (activeOnly) {
      arr = arr.filter(
        (r) =>
          (Number(r.GMV_net || r.SalesTotal || 0) || 0) > 0 ||
          (Number(r.TotalTickets || 0) || 0) > 0,
      );
    }

    const gmv = arr.reduce(
      (s, r) => s + (Number(r.GMV_net || r.SalesTotal || 0) || 0),
      0,
    );
    const commission = arr.reduce(
      (s, r) => s + (Number(r.CommissionAmount || 0) || 0),
      0,
    );
    const transactions = arr.reduce(
      (s, r) => s + (Number(r.TotalTickets || 0) || 0),
      0,
    );
    const ticketAvg = transactions > 0 ? gmv / transactions : null;
    return { gmv, commission, transactions, ticketAvg, rows: arr };
  }

  aggregateOperational(opts = {}) {
    const {
      siteFilter,
      activeOnly,
      businessModel: bmState,
      country,
    } = this.state;
    const ignoreActive = opts.ignoreActive || false;
    // allow callers to override which business model filter is used
    const businessModel =
      opts.businessModel !== undefined ? opts.businessModel : bmState;
    let arr = Array.isArray(this.props.generatedOperationalArqueos)
      ? this.props.generatedOperationalArqueos
      : [];
    // Always exclude this CustomerId from operational arqueos
    const EXCLUDE_CUSTOMER_ID_OP = "620d1664db950220fabc0a41"; //VACWAY TECH
    arr = arr.filter((r) => {
      const cid = (r.CustomerId || r.Customer || "") + "";
      return cid !== EXCLUDE_CUSTOMER_ID_OP;
    });

    // apply business-model filter client-side as well
    if (businessModel) {
      const map = {
        revenue_share: "REVENUE SHARE",
        alquiler: "RENTAL",
        venta: "SALE",
      };
      const bm = map[businessModel] || businessModel;
      arr = arr.filter((r) => r.BusinessModel === bm);
    }
    // apply country filter
    if (country && country.trim()) {
      const q = country.trim().toLowerCase();
      arr = arr.filter((r) =>
        (r.Country || r.CustomerCountry || r.SiteCountry || "")
          .toString()
          .toLowerCase()
          .includes(q),
      );
    }

    // client-only vertical filter (no server fetch)
    const { vertical } = this.state || {};
    if (vertical && vertical.trim()) {
      const q = vertical.trim().toLowerCase();
      arr = arr.filter((r) =>
        ((r.Category || r.Vertical || r.CategoryName || "") + "")
          .toString()
          .toLowerCase()
          .includes(q),
      );
    }

    if (siteFilter && siteFilter.trim()) {
      const q = siteFilter.trim();
      arr = arr.filter((r) => this.rowMatchesFilter(r, q));
    }

    // Spec: Activa si GMV > 0 en el periodo, o Transacciones > 0 en el periodo, o reporting
    if (activeOnly && !ignoreActive) {
      arr = arr.filter(
        (r) =>
          Number(r.GMV_net || r.SalesTotal || 0) > 0 ||
          Number(r.TotalTickets || 0) > 0 ||
          !!r.isReporting,
      );
    }

    // Build a Day|MachineSerial → TotalTickets lookup from the filtered sales
    // arqueos. When LockersOccupied is absent/0 in the operational row (common
    // in many deployments) we fall back to TotalTickets as a proxy for the
    // number of lockers that were actually occupied on that day.
    const _salesRowsForOcc = Array.isArray(opts.salesRows)
      ? opts.salesRows
      : this.aggregateSales().rows || [];
    const _salesOccLookup = {};
    _salesRowsForOcc.forEach((r) => {
      const day = r.Day
        ? r.Day.slice
          ? r.Day.slice(0, 10)
          : String(r.Day).slice(0, 10)
        : "";
      const machine = (r.MachineSerial || r.MachineId || r.Serial || "") + "";
      const key = `${day}|${machine}`;
      _salesOccLookup[key] =
        (_salesOccLookup[key] || 0) + (Number(r.TotalTickets || 0) || 0);
    });

    // occupancy adjusted per doc = LockersOccupied / (TotlockerStatus.total - TotlockerStatus.fault)
    // Falls back to TotalTickets (sales) when LockersOccupied is missing.
    const occs = arr.map((r) => {
      const total =
        (r.TotlockerStatus && r.TotlockerStatus.total) || r.TotalLockers || 0;
      const fault = (r.TotlockerStatus && r.TotlockerStatus.fault) || 0;
      const denom = total - fault;
      const day = r.Day
        ? r.Day.slice
          ? r.Day.slice(0, 10)
          : String(r.Day).slice(0, 10)
        : "";
      const machine = (r.MachineSerial || r.MachineId || r.Serial || "") + "";
      const lockersOccupied =
        Number(r.LockersOccupied || 0) ||
        _salesOccLookup[`${day}|${machine}`] ||
        0;
      const occ = denom > 0 ? lockersOccupied / denom : null;
      const avail = total > 0 ? 1 - fault / total : null;
      return {
        occ,
        avail,
        total,
        fault,
        site: r.Location,
        machine: r.MachineSerial,
        isReporting: !!r.isReporting,
        incidencesOpened: Number(r.IncidencesOpened || 0),
        Day: r.Day,
      };
    });

    const avgOcc = occs
      .filter((o) => o.occ !== null)
      .reduce((s, o) => s + o.occ, 0);
    const occCount = occs.filter((o) => o.occ !== null).length;
    const avgOccFinal = occCount > 0 ? avgOcc / occCount : null;

    const avgAvail = occs
      .filter((o) => o.avail !== null)
      .reduce((s, o) => s + o.avail, 0);
    const availCount = occs.filter((o) => o.avail !== null).length;
    const avgAvailFinal = availCount > 0 ? avgAvail / availCount : null;

    const totalFaulty = occs.reduce((s, o) => s + (o.fault || 0), 0);
    const totalLockers = occs.reduce((s, o) => s + (o.total || 0), 0);

    const machinesReporting = occs.filter((o) => o.isReporting).length;
    const machinesTotal = occs.length;

    const incidences = occs.reduce((s, o) => s + (o.incidencesOpened || 0), 0);

    return {
      avgOccFinal,
      avgAvailFinal,
      totalFaulty,
      totalLockers,
      machinesReporting,
      machinesTotal,
      incidences,
      occs,
      raw: arr,
    };
  }

  // Average lockers available per day across the selected period (avg_dias)
  computeAvgLockersAvailable() {
    const opRows = this.aggregateOperational().raw || [];
    if (!opRows.length) return null;

    // group by day
    const byDay = {};
    opRows.forEach((r) => {
      const day = r.Day
        ? r.Day.slice
          ? r.Day.slice(0, 10)
          : String(r.Day).slice(0, 10)
        : "unknown";
      const total =
        (r.TotlockerStatus && r.TotlockerStatus.total) || r.TotalLockers || 0;
      const fault = (r.TotlockerStatus && r.TotlockerStatus.fault) || 0;
      const available = Math.max(0, total - fault);
      byDay[day] = (byDay[day] || 0) + available;
    });

    const days = Object.keys(byDay);
    if (!days.length) return null;
    const sum = days.reduce((s, d) => s + byDay[d], 0);
    return sum / days.length;
  }

  // same as computeAvgLockersAvailable but over arbitrary rows
  computeAvgLockersAvailableFromRows(rows) {
    const arr = Array.isArray(rows) ? rows : [];
    if (!arr.length) return null;

    const byDay = {};
    arr.forEach((r) => {
      const day = r.Day
        ? r.Day.slice
          ? r.Day.slice(0, 10)
          : String(r.Day).slice(0, 10)
        : "unknown";
      const total =
        (r.TotlockerStatus && r.TotlockerStatus.total) || r.TotalLockers || 0;
      const fault = (r.TotlockerStatus && r.TotlockerStatus.fault) || 0;
      const available = Math.max(0, total - fault);
      byDay[day] = (byDay[day] || 0) + available;
    });

    const days = Object.keys(byDay);
    if (!days.length) return null;
    const sum = days.reduce((s, d) => s + byDay[d], 0);
    return sum / days.length;
  }

  // Aggregate operational metrics from arbitrary rows (used for LY comparisons)
  aggregateOperationalFromRows(rows, salesRows = []) {
    const { businessModel, siteFilter, country, vertical, activeOnly } = this.state;
    const EXCLUDE_CUSTOMER_ID = "620d1664db950220fabc0a41"; // VACWAY TECH
    let arr = Array.isArray(rows) ? rows.slice() : [];
    arr = arr.filter((r) => ((r.CustomerId || r.Customer || "") + "") !== EXCLUDE_CUSTOMER_ID);
    if (businessModel) {
      const map = {
        revenue_share: "REVENUE SHARE",
        alquiler: "RENTAL",
        venta: "SALE",
      };
      const bm = map[businessModel] || businessModel;
      arr = arr.filter((r) => r.BusinessModel === bm);
    }
    if (country && country.trim()) {
      const q = country.trim().toLowerCase();
      arr = arr.filter((r) =>
        (r.Country || r.CustomerCountry || r.SiteCountry || "")
          .toString()
          .toLowerCase()
          .includes(q),
      );
    }
    if (vertical && vertical.trim()) {
      const q = vertical.trim().toLowerCase();
      arr = arr.filter((r) =>
        ((r.Category || r.Vertical || r.CategoryName || "") + "")
          .toString()
          .toLowerCase()
          .includes(q),
      );
    }
    if (siteFilter && siteFilter.trim()) {
      const q = siteFilter.trim();
      arr = arr.filter((r) => this.rowMatchesFilter(r, q));
    }

    // Match aggregateOperational: only keep rows with activity when activeOnly is on
    if (activeOnly) {
      arr = arr.filter(
        (r) =>
          Number(r.GMV_net || r.SalesTotal || 0) > 0 ||
          Number(r.TotalTickets || 0) > 0 ||
          !!r.isReporting,
      );
    }

    // Same TotalTickets fallback as aggregateOperational
    const _salesOccLookup = {};
    (Array.isArray(salesRows) ? salesRows : []).forEach((r) => {
      const day = r.Day
        ? r.Day.slice
          ? r.Day.slice(0, 10)
          : String(r.Day).slice(0, 10)
        : "";
      const machine = (r.MachineSerial || r.MachineId || r.Serial || "") + "";
      const key = `${day}|${machine}`;
      _salesOccLookup[key] =
        (_salesOccLookup[key] || 0) + (Number(r.TotalTickets || 0) || 0);
    });

    const occs = arr.map((r) => {
      const total =
        (r.TotlockerStatus && r.TotlockerStatus.total) || r.TotalLockers || 0;
      const fault = (r.TotlockerStatus && r.TotlockerStatus.fault) || 0;
      const denom = total - fault;
      const day = r.Day
        ? r.Day.slice
          ? r.Day.slice(0, 10)
          : String(r.Day).slice(0, 10)
        : "";
      const machine = (r.MachineSerial || r.MachineId || r.Serial || "") + "";
      const lockersOccupied =
        Number(r.LockersOccupied || 0) ||
        _salesOccLookup[`${day}|${machine}`] ||
        0;
      const occ = denom > 0 ? lockersOccupied / denom : null;
      const avail = total > 0 ? 1 - fault / total : null;
      return {
        occ,
        avail,
        total,
        fault,
        site: r.Location,
        machine: r.MachineSerial,
        isReporting: !!r.isReporting,
        incidencesOpened: Number(r.IncidencesOpened || 0),
        Day: r.Day,
      };
    });

    const avgOcc = occs
      .filter((o) => o.occ !== null)
      .reduce((s, o) => s + o.occ, 0);
    const occCount = occs.filter((o) => o.occ !== null).length;
    const avgOccFinal = occCount > 0 ? avgOcc / occCount : null;

    const avgAvail = occs
      .filter((o) => o.avail !== null)
      .reduce((s, o) => s + o.avail, 0);
    const availCount = occs.filter((o) => o.avail !== null).length;
    const avgAvailFinal = availCount > 0 ? avgAvail / availCount : null;

    const totalFaulty = occs.reduce((s, o) => s + (o.fault || 0), 0);
    const totalLockers = occs.reduce((s, o) => s + (o.total || 0), 0);

    const machinesReporting = occs.filter((o) => o.isReporting).length;
    const machinesTotal = occs.length;

    const incidences = occs.reduce((s, o) => s + (o.incidencesOpened || 0), 0);

    return {
      avgOccFinal,
      avgAvailFinal,
      totalFaulty,
      totalLockers,
      machinesReporting,
      machinesTotal,
      incidences,
      occs,
      raw: arr,
    };
  }

  groupBySiteTop5(salesRows) {
    const map = {};
    salesRows.forEach((r) => {
      const site = r.Site || r.Location || r.SiteName || "Unknown";
      if (!map[site])
        map[site] = {
          site,
          gmv: 0,
          commission: 0,
          transactions: 0,
          occupancySum: 0,
          occupancyCount: 0,
          incidents: 0,
        };
      map[site].gmv += Number(r.GMV_net || r.SalesTotal || 0) || 0;
      map[site].commission += Number(r.CommissionAmount || 0) || 0;
      map[site].transactions += Number(r.TotalTickets || 0) || 0;
      map[site].incidents += Number(r.IncidencesOpened || 0) || 0;
    });
    const arr = Object.values(map);
    arr.sort((a, b) => b.gmv - a.gmv);
    return arr.slice(0, 5);
  }

  // base installed donuts computed from operational docs (latest snapshot within the selected range)
  // helper used to normalize the various fields we treat as the site/customer
  // identifier. this ensures every filter and datalist uses the same set of
  // properties (Fullname, SiteName, Site, Location, CustomerName, MachineSerial).
  // NOTE: old `siteString` helper removed – filtering now only uses
  // the explicit `CustomerName` field per user request.

  computeInstalledBy(arr, key, asOf) {
    // We want the latest snapshot for each machine, not every day of the range.
    // Take the row with the maximum `Day` value per machine and aggregate that.
    const latest = {};
    // allow calling code to specify the date at which the snapshot should be
    // evaluated (compare period end), otherwise fall back to now.
    const today = asOf ? new Date(asOf) : new Date();

    arr.forEach((r) => {
      // Only include machines that are installed (installed_at <= today) and not removed (removed_at null or > today)
      if (
        (r.installed_at && new Date(r.installed_at) > today) ||
        (r.removed_at && new Date(r.removed_at) <= today)
      ) {
        return;
      }
      const machineId =
        r.MachineSerial || r.MachineId || r.Serial || r._id || "unknown";
      const day = r.Day ? new Date(r.Day) : null;
      if (
        !latest[machineId] ||
        (day && latest[machineId].Day && day > new Date(latest[machineId].Day))
      ) {
        latest[machineId] = r;
      }
    });

    const map = {};
    Object.values(latest).forEach((r) => {
      const group = r[key] || "Unknown";
      const total =
        (r.TotlockerStatus && r.TotlockerStatus.total) || r.TotalLockers || 0;
      map[group] = (map[group] || 0) + (total || 0);
    });

    const items = Object.keys(map).map((k) => ({ key: k, value: map[k] }));
    const total = items.reduce((s, i) => s + i.value, 0);
    // `overallTotal` was never defined here; we only compute the raw total and
    // percentages. Any display override is handled by the caller.
    items.forEach((i) => (i.pct = total > 0 ? i.value / total : 0));
    items.sort((a, b) => b.value - a.value);
    return { items, total };
  }

  // Return latest row per machineId (keep the row with max Day), optionally
  // ignoring install/removal dates relative to `asOf`. Used to compute
  // pre-filter installed totals without counting the same machine multiple
  // times across different days.
  dedupeLatestPerMachine(rows, asOf) {
    const latest = {};
    const arr = Array.isArray(rows) ? rows : [];
    arr.forEach((r) => {
      const machineId = (r.MachineSerial || r.MachineId || r.Serial || r._id || "") + "";
      // Skip machines not installed at `asOf` or already removed at `asOf`.
      if (
        asOf &&
        ((r.installed_at && new Date(r.installed_at) > asOf) ||
          (r.removed_at && new Date(r.removed_at) <= asOf))
      ) {
        return;
      }
      const day = r.Day ? new Date(r.Day) : null;
      if (!latest[machineId] || (day && latest[machineId].Day && day > new Date(latest[machineId].Day))) {
        latest[machineId] = r;
      }
    });
    return Object.values(latest);
  }

  // Render a single installed-donut card (KPI-style header + two-column body)
  // extended with optional compareData: {items,total}
  renderInstalledDonutCard(
    title,
    data,
    groupKey,
    iconName = "layer-group",
    overallTotal = null,
    compareData = null,
  ) {
    const colors = [
      "#0b97ff", // bright blue
      "#06b6d4", // cyan
      "#10b981", // teal/green
      "#f59e0b", // amber
      "#ec4899", // pink
      "#8b5cf6", // violet
      "#f97316", // orange
      "#ef4444", // red
      "#6366f1", // indigo
    ];
    const makeItems = (src) => {
      const base = (src.items || []).slice(0, 6);
      const oth = Math.max(
        0,
        (src.total || 0) - base.reduce((s, i) => s + i.value, 0),
      );
      return oth > 0
        ? [...base, { key: "Other", value: oth, pct: oth / (src.total || 1) }]
        : base;
    };
    const items = makeItems(data);
    const chartData = items.map((i) => ({
      name: i.key,
      value: i.value,
      pct: i.pct,
    }));
    const total = data.total || chartData.reduce((s, i) => s + i.value, 0);
    const displayTotal = overallTotal !== null ? overallTotal : total;

    // prepare comparison chart info if provided and non-empty
    let compareChartData = null;
    let compareTotal = 0;
    if (compareData && (compareData.total || 0) > 0) {
      const compItems = makeItems(compareData);
      compareTotal =
        compareData.total || compItems.reduce((s, i) => s + i.value, 0);
      // ignore if there are no meaningful slices
      if (compItems.length > 0) {
        compareChartData = compItems.map((i) => ({
          name: i.key,
          value: i.value,
          pct: i.pct,
        }));
      }
    }

    // capture chartData for connector calculation after paint
    this.latestChartData = this.latestChartData || {};
    this.latestChartData[groupKey] = chartData;
    this.legendItemRefs[groupKey] = [];

    // show everything (no toggling / hide/visible labels)
    const visibleChartData = chartData;

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {/* header: icon + title (KPI-like) */}
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <i
            className={`fas fa-${iconName} mr-2`}
            style={{ color: "#6b7280" }}
          ></i>
          <div style={{ fontWeight: 700 }}>{title}</div>
        </div>

        {/* body: two columns — donut (left) + list (right) */}
        <div
          ref={(el) => (this.containerRefs[groupKey] = el)}
          className="donut-container"
          style={{
            position: "relative",
            display: "flex",
            gap: 10,
            alignItems: "flex-start",
            marginTop: 30,
            // wrapping controlled by CSS media query
          }}
        >
          <div
            ref={(el) => (this.donutRefs[groupKey] = el)}
            style={{
              width: 320,
              height: 220,
              position: "relative",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <PieChart width={320} height={220}>
              {/* outer ring: current period */}
              <Pie
                data={visibleChartData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius={65}
                outerRadius={90}
                paddingAngle={2}
                label={({ percent }) => `${(percent * 100).toFixed(1)}%`}
                labelLine={{
                  stroke: "#6b7280",
                  strokeDasharray: "3 4",
                  strokeWidth: 1,
                }}
              >
                {visibleChartData.map((entry, idx) => (
                  <Cell
                    key={`cell-${groupKey}-${idx}`}
                    fill={colors[idx % colors.length]}
                  />
                ))}
              </Pie>
              {/* inner ring: comparison period */}
              {compareChartData && (
                <Pie
                  data={compareChartData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={55}
                  paddingAngle={1}
                  label={false} // no labels on inner donut
                  labelLine={false}
                >
                  {compareChartData.map((entry, idx) => (
                    <Cell
                      key={`cell2-${groupKey}-${idx}`}
                      fill={colors[idx % colors.length]}
                      fillOpacity={0.5}
                    />
                  ))}
                </Pie>
              )}
              <Tooltip
                formatter={(value, name) => [
                  `${value} (${total > 0 ? ((value / total) * 100).toFixed(1) : 0}%)`,
                  name,
                ]}
                wrapperStyle={{ zIndex: 10 }}
              />
            </PieChart>
            {/* central total overlay */}
            <div
              style={{
                position: "absolute",
                textAlign: "center",
                pointerEvents: "none",
                zIndex: 1, // keep below tooltip
              }}
            >
              <div style={{ fontSize: 16, fontWeight: 700 }}>
                {displayTotal}
              </div>
              {compareChartData && (
                <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
                  vs {compareTotal}
                </div>
              )}
              <div style={{ fontSize: 12, color: "#6b7280" }}>Total</div>
            </div>
          </div>

          <div style={{ flex: 1, minWidth: 280 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {items.map((it, idx) => {
                const compItem =
                  compareChartData && compareChartData.length > 0
                    ? compareChartData.find((c) => c.name === it.key)
                    : null;
                return (
                  <div
                    key={it.key}
                    ref={(el) => (this.legendItemRefs[groupKey][idx] = el)}
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 10,
                    }}
                  >
                    <div
                      style={{
                        width: 12,
                        height: 12,
                        background: colors[idx % colors.length],
                        borderRadius: 3,
                        marginTop: 3,
                      }}
                    />
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        marginLeft: 6,
                      }}
                    >
                      <div style={{ fontSize: 13, fontWeight: 700 }}>
                        {it.key}
                      </div>
                      <div
                        className="text-muted small"
                        style={{ marginTop: 2 }}
                      >
                        {it.value} {`(${(it.pct * 100).toFixed(1)}%)`}
                        {compItem && compItem.value != null && (
                          <>
                            {" "}
                            vs {compItem.value}{" "}
                            {`(${(compItem.pct * 100).toFixed(1)}%)`}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  }

  renderKPIs() {
    const { gmv, commission, transactions, ticketAvg } = this.aggregateSales();
    const op = this.aggregateOperational();
    const role = (this.props.role || "").toString().toUpperCase();
    const bmFilter = (this.state.businessModel || "").toString();
    const isRevenueShareFilter = bmFilter === "revenue_share" || bmFilter.toUpperCase() === "REVENUE SHARE";

    // When role is ADMIN and the dashboard is filtered to REVENUE SHARE we display
    // the opposite side of the commission/take-rate (platform cut). Other
    // business models are shown unchanged.
    const displayedCommission = (() => {
      if (gmv == null || commission == null) return commission;
      // show customer commission (do not invert for ADMIN)
      return commission;
    })();

    const displayedTakeRate = (() => {
      if (!gmv || displayedCommission == null) return null;
      return Number(displayedCommission) / Number(gmv);
    })();
    return (
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        <div className="card p-3" style={{ minWidth: 180 }}>
          <div className="text-muted">GMV</div>
          <div style={{ fontSize: 20, fontWeight: 700 }}>
            {fmtCurrency(gmv)} €
          </div>
        </div>
        <div className="card p-3" style={{ minWidth: 180 }}>
          <div className="text-muted">Comisión neta</div>
          <div style={{ fontSize: 20, fontWeight: 700 }}>
            {fmtCurrency(displayedCommission)} €
          </div>
        </div>
        <div className="card p-3" style={{ minWidth: 180 }}>
          <div className="text-muted">Take rate</div>
          <div style={{ fontSize: 20, fontWeight: 700 }}>
            {displayedTakeRate != null && gmv ? pct(displayedTakeRate) : "—"}
          </div>
        </div>
        <div className="card p-3" style={{ minWidth: 180 }}>
          <div className="text-muted">Transacciones</div>
          <div style={{ fontSize: 20, fontWeight: 700 }}>{transactions}</div>
        </div>
        <div className="card p-3" style={{ minWidth: 180 }}>
          <div className="text-muted">Ticket medio</div>
          <div style={{ fontSize: 20, fontWeight: 700 }}>
            {ticketAvg ? fmtCurrency(ticketAvg) + " €" : "—"}
          </div>
        </div>
        <div className="card p-3" style={{ minWidth: 220 }}>
          <div className="text-muted">Ocupación ajustada (promedio)</div>
          <div style={{ fontSize: 20, fontWeight: 700 }}>
            {op.avgOccFinal !== null ? pct(op.avgOccFinal) : "—"}
          </div>
        </div>
        <div className="card p-3" style={{ minWidth: 180 }}>
          <div className="text-muted">Disponibilidad (promedio)</div>
          <div style={{ fontSize: 20, fontWeight: 700 }}>
            {op.avgAvailFinal !== null ? pct(op.avgAvailFinal) : "—"}
          </div>
        </div>
        <div className="card p-3" style={{ minWidth: 180 }}>
          <div className="text-muted">Incidencias</div>
          <div style={{ fontSize: 20, fontWeight: 700 }}>{op.incidences}</div>
        </div>
        <div className="card p-3" style={{ minWidth: 220 }}>
          <div className="text-muted">% máquinas reportando (hoy)</div>
          <div style={{ fontSize: 20, fontWeight: 700 }}>
            {(() => {
              const rq = this.computeReportingQuality();
              return rq.total > 0
                ? `${Math.round(rq.pct * 100)}% (${rq.reporting}/${rq.total})`
                : "—";
            })()}
          </div>
        </div>
      </div>
    );
  }

  // Combined GMV + Commission trend used by the 'trend' panel
  // compute daily per-locker and totals for evolution charts
  computeDailyMetrics() {
    const sales = this.aggregateSales().rows || [];
    const opRows = this.aggregateOperational().raw || [];
    const byDaySales = {};
    sales.forEach((r) => {
      const day = r.Day
        ? r.Day.slice
          ? r.Day.slice(0, 10)
          : String(r.Day).slice(0, 10)
        : "unknown";
      if (!byDaySales[day]) byDaySales[day] = { gmv: 0, commission: 0 };
      // use gross GMV for metrics, falling back to SalesTotal for legacy rows
      byDaySales[day].gmv += Number(r.GMV_net || r.SalesTotal || 0) || 0;
      byDaySales[day].commission += Number(r.CommissionAmount || 0) || 0;
    });
    const byDayAvail = {};
    opRows.forEach((r) => {
      const day = r.Day
        ? r.Day.slice
          ? r.Day.slice(0, 10)
          : String(r.Day).slice(0, 10)
        : "unknown";
      const total =
        (r.TotlockerStatus && r.TotlockerStatus.total) || r.TotalLockers || 0;
      const fault = (r.TotlockerStatus && r.TotlockerStatus.fault) || 0;
      const avail = Math.max(0, total - fault);
      byDayAvail[day] = (byDayAvail[day] || 0) + avail;
    });
    const days = Array.from(
      new Set([...Object.keys(byDaySales), ...Object.keys(byDayAvail)]),
    ).sort();
    return days.map((d) => {
      const totals = byDaySales[d] || { gmv: 0, commission: 0 };
      const avail = byDayAvail[d] || 0;
      return {
        day: d,
        gmvPer: avail > 0 ? totals.gmv / avail : null,
        comPer: avail > 0 ? totals.commission / avail : null,
        gmv: totals.gmv,
        commission: totals.commission,
      };
    });
  }

  renderLockerCard(
    label,
    value,
    chartData,
    type = "bar",
    icon = "chart-bar",
    deltaPct = null,
    options = {},
  ) {
    // optionally hide entire card if there is nothing to show
    if (options.hideIfEmpty) {
      const emptyData = !chartData || chartData.length === 0;
      if (emptyData) {
        return null;
      }
    }

    const formatted =
      value !== null && value !== undefined ? `${fmtCurrency(value)} €` : "—";

    // determine if we should display a delta row at all
    const { compareStartDate, compareEndDate, lyPersistedHasData } = this.state;
    const hasCompareRange = compareStartDate && compareEndDate;

    let deltaElement = null;
    if (hasCompareRange) {
      if (deltaPct !== null && deltaPct !== undefined && lyPersistedHasData) {
        const pos = deltaPct > 0;
        const color = pos
          ? "text-success"
          : deltaPct < 0
            ? "text-danger"
            : "text-muted";
        const sign = pos ? "+" : deltaPct < 0 ? "-" : "";
        deltaElement = (
          <div
            className="small text-muted"
            style={{
              marginTop: 2,
              display: "flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            <span>vs&nbsp;LY:</span>
            <span className={color}>
              {sign}
              {Math.abs((deltaPct * 100).toFixed(1))}%
            </span>
          </div>
        );
      } else {
        // comparison requested but either no persisted data or delta not computable
        deltaElement = (
          <div className="small text-muted" style={{ marginTop: 2 }}>
            vs LY: n/a
          </div>
        );
      }
    }

    const showValueRow = value !== null && value !== undefined;

    return (
      <div
        className="dashboard-card-section"
        style={{
          display: "flex",
          minHeight: type === "line" ? 340 : 140,
          height: "100%",
          flexDirection: "column",
          padding: "12px",
          boxSizing: "border-box",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <i className={`fas fa-${icon} mr-2`} style={{ color: "#6b7280" }}></i>
          <div style={{ fontWeight: 700 }}>{label}</div>
        </div>

        {showValueRow && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              margin: "6px 0",
            }}
          >
            <div
              className="kpi-value"
              style={{ fontSize: 24, fontWeight: 700 }}
            >
              {formatted}
            </div>
            {deltaElement}
          </div>
        )}

        {type === "bar" ? (
          <></>
        ) : // <ResponsiveContainer>
        //   <BarChart
        //     data={chartData}
        //     margin={{ top: 0, right: 0, left: 0, bottom: 0 }}
        //   >
        //     <XAxis
        //       dataKey="name"
        //       tick={{ fontSize: 10, fill: "#6b7280" }}
        //       interval={0}
        //       angle={-45}
        //       textAnchor="end"
        //       height={40}
        //     />
        //     <YAxis tick={{ fontSize: 10, fill: "#6b7280" }} />
        //     <Tooltip formatter={(v) => `${fmtCurrency(v)} €`} />
        //     <Bar dataKey="value" fill="#2563EB" />
        //   </BarChart>
        // </ResponsiveContainer>
        // if no trend data, show placeholder rather than empty flat lines
        !chartData || chartData.length === 0 ? (
          <div
            style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#6b7280",
              fontSize: 12,
            }}
          >
            sin variación
          </div>
        ) : (
          <div
            style={{
              width: "100%",
              flex: type === "line" ? 1 : undefined,
              height: type === "line" ? undefined : type === "bar" ? 80 : 60,
              minHeight: type === "line" ? 220 : undefined,
            }}
          >
            <ResponsiveContainer>
              <LineChart
                data={chartData}
                margin={{ top: 0, right: 0, left: 0, bottom: 0 }}
              >
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 10, fill: "#6b7280" }}
                  interval={0}
                  angle={-45}
                  textAnchor="end"
                  height={40}
                />
                <YAxis tick={{ fontSize: 10, fill: "#6b7280" }} />
                <CartesianGrid strokeDasharray="3 3" />
                <Legend layout="horizontal" verticalAlign="top" height={20} />
                <Tooltip formatter={(v) => v != null ? `${fmtCurrency(v)} €` : '—'} />
                <Line
                  type="monotone"
                  dataKey="gmv"
                  name="GMV"
                  stroke="#2563EB"
                  dot={{ r: 2 }}
                />
                <Line
                  type="monotone"
                  dataKey="commission"
                  name="Comisión"
                  stroke="#059669"
                  dot={{ r: 2 }}
                />
                {(options.showLy) && (
                  <Line
                    type="monotone"
                    dataKey="lyGmv"
                    name="GMV LY"
                    stroke="#93c5fd"
                    strokeDasharray="5 3"
                    dot={false}
                    connectNulls
                  />
                )}
                {(options.showLy) && (
                  <Line
                    type="monotone"
                    dataKey="lyCommission"
                    name="Comisión LY"
                    stroke="#6ee7b7"
                    strokeDasharray="5 3"
                    dot={false}
                    connectNulls
                  />
                )}
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    );
  }

  renderLockerKPIs() {
    const daily = this.computeDailyMetrics();

    // Formula: SUM of (value_d / lockers_d) across all days with locker data.
    // i.e. (GMV_d1/L_d1) + (GMV_d2/L_d2) + ... (no division by N)
    // computeDailyMetrics() already provides gmvPer = GMV_d / avail_d
    // and comPer = commission_d / avail_d, so just sum those.
    const gmvDays = daily.filter((d) => d.gmvPer !== null && d.gmvPer !== undefined);
    const comDays = daily.filter((d) => d.comPer !== null && d.comPer !== undefined);

    const perLockerGMV =
      gmvDays.length > 0
        ? gmvDays.reduce((s, d) => s + d.gmvPer, 0)
        : null;
    const perLockerCom =
      comDays.length > 0
        ? comDays.reduce((s, d) => s + d.comPer, 0)
        : null;

    // Helper to build daily per-locker metrics from arbitrary sales + operational rows.
    // Mirrors computeDailyMetrics() exactly: only machines present in the sales rows
    // (GMV > 0 or tickets > 0) are counted as "active" when building the available
    // locker denominator — matching the activeOnly filter used by aggregateOperational().
    const computeDailyPerLocker = (salesRows, opRows) => {
      // Expects already-filtered rows (same filters as current period).
      const byDaySales = {};
      (salesRows || []).forEach((r) => {
        const day = r.Day ? (r.Day.slice ? r.Day.slice(0, 10) : String(r.Day).slice(0, 10)) : 'unknown';
        if (!byDaySales[day]) byDaySales[day] = { gmv: 0, commission: 0 };
        byDaySales[day].gmv += Number(r.GMV_net || r.SalesTotal || 0) || 0;
        byDaySales[day].commission += Number(r.CommissionAmount || 0) || 0;
      });
      const byDayAvail = {};
      (opRows || []).forEach((r) => {
        const day = r.Day ? (r.Day.slice ? r.Day.slice(0, 10) : String(r.Day).slice(0, 10)) : 'unknown';
        const total = (r.TotlockerStatus && r.TotlockerStatus.total) || r.TotalLockers || 0;
        const fault = (r.TotlockerStatus && r.TotlockerStatus.fault) || 0;
        byDayAvail[day] = (byDayAvail[day] || 0) + Math.max(0, total - fault);
      });
      const allDays = Array.from(new Set([...Object.keys(byDaySales), ...Object.keys(byDayAvail)])).sort();
      let gmvSum = 0, comSum = 0, count = 0;
      allDays.forEach((d) => {
        const avail = byDayAvail[d] || 0;
        if (avail > 0) {
          const s = byDaySales[d] || { gmv: 0, commission: 0 };
          const dayGmvPer = s.gmv / avail;
          const dayComPer = s.commission / avail;
          gmvSum += dayGmvPer;
          comSum += dayComPer;
          count++;
        }
      });
      return {
        gmv: count > 0 ? gmvSum : null,
        com: count > 0 ? comSum : null,
      };
    };

    // compute last-year equivalents if present
    let lyPerLockerGMV = null;
    let lyPerLockerCom = null;

    // Shared helper: apply the SAME client-side filters that aggregateSales() /
    // aggregateOperational() use (businessModel, country, vertical, siteFilter,
    // activeOnly, VACWAY exclude) so LY data is comparable to the current period.
    const EXCLUDE_CID = "620d1664db950220fabc0a41";
    const { businessModel, country, vertical, siteFilter, activeOnly } = this.state;
    const bmMap = { revenue_share: "REVENUE SHARE", alquiler: "RENTAL", venta: "SALE" };
    const filterSalesRows = (rows, opRowsHint) => {
      let arr = (rows || []).slice();
      arr = arr.filter((r) => ((r.CustomerId || r.Customer || "") + "") !== EXCLUDE_CID);
      if (businessModel) {
        const bm = bmMap[businessModel] || businessModel;
        arr = arr.filter((r) => r.BusinessModel === bm);
      }
      if (country && country.trim()) {
        const q = country.trim().toLowerCase();
        arr = arr.filter((r) => (r.CustomerCountry || r.Country || r.SiteCountry || "").toString().toLowerCase().includes(q));
      }
      if (vertical && vertical.trim()) {
        const q = vertical.trim().toLowerCase();
        const opRowsForLookup = Array.isArray(opRowsHint)
          ? opRowsHint
          : Array.isArray(this.props.generatedOperationalArqueos)
            ? this.props.generatedOperationalArqueos
            : [];
        const machineCatMap = {};
        opRowsForLookup.forEach((r) => {
          const mid = (r.MachineSerial || r.MachineId || r.Serial || "") + "";
          if (mid && (r.Category || r.Vertical || r.CategoryName)) {
            machineCatMap[mid] = ((r.Category || r.Vertical || r.CategoryName) + "").toLowerCase();
          }
        });
        arr = arr.filter((r) => {
          const own = ((r.Category || r.Vertical || r.CategoryName || "") + "").toLowerCase();
          if (own.includes(q)) return true;
          const mid = (r.MachineSerial || r.MachineId || r.Serial || "") + "";
          return (machineCatMap[mid] || "").includes(q);
        });
      }
      if (siteFilter && siteFilter.trim()) {
        const q = siteFilter.trim();
        arr = arr.filter((r) => this.rowMatchesFilter(r, q));
      }
      if (activeOnly) {
        arr = arr.filter((r) =>
          (Number(r.GMV_net || r.SalesTotal || 0) || 0) > 0 ||
          (Number(r.TotalTickets || 0) || 0) > 0
        );
      }
      return arr;
    };
    const filterOpRows = (rows) => {
      let arr = (rows || []).slice();
      arr = arr.filter((r) => ((r.CustomerId || r.Customer || "") + "") !== EXCLUDE_CID);
      if (businessModel) {
        const bm = bmMap[businessModel] || businessModel;
        arr = arr.filter((r) => r.BusinessModel === bm);
      }
      if (country && country.trim()) {
        const q = country.trim().toLowerCase();
        arr = arr.filter((r) => (r.Country || r.CustomerCountry || r.SiteCountry || "").toString().toLowerCase().includes(q));
      }
      if (vertical && vertical.trim()) {
        const q = vertical.trim().toLowerCase();
        arr = arr.filter((r) => ((r.Category || r.Vertical || r.CategoryName || "") + "").toString().toLowerCase().includes(q));
      }
      if (siteFilter && siteFilter.trim()) {
        const q = siteFilter.trim();
        arr = arr.filter((r) => this.rowMatchesFilter(r, q));
      }
      if (activeOnly) {
        arr = arr.filter((r) =>
          Number(r.GMV_net || r.SalesTotal || 0) > 0 ||
          Number(r.TotalTickets || 0) > 0 ||
          !!r.isReporting
        );
      }
      return arr;
    };

    // Apply all filters to LY data (same filters as current period)
    // Pass LY operational rows as the category-lookup source for the vertical filter
    const lySalesFiltered = filterSalesRows(this.state.lySalesArqueos, this.state.lyOperationalArqueos);
    const lyOpFiltered = filterOpRows(this.state.lyOperationalArqueos);

    if (
      Array.isArray(this.state.lySalesArqueos) &&
      Array.isArray(this.state.lyOperationalArqueos)
    ) {
      if (this.state.lyPersistedHasData) {
        const lyDaily = computeDailyPerLocker(lySalesFiltered, lyOpFiltered);
        lyPerLockerGMV = lyDaily.gmv;
        lyPerLockerCom = lyDaily.com;
      }
    }

    const deltaGmvPct =
      lyPerLockerGMV !== null && lyPerLockerGMV !== 0
        ? (perLockerGMV - lyPerLockerGMV) / (lyPerLockerGMV || 1)
        : null;
    const deltaComPct =
      lyPerLockerCom !== null && lyPerLockerCom !== 0
        ? (perLockerCom - lyPerLockerCom) / (lyPerLockerCom || 1)
        : null;

    const chart1 = daily.map((d) => ({ name: d.day, value: d.gmvPer || 0 }));
    const chart2 = daily.map((d) => ({ name: d.day, value: d.comPer || 0 }));
    // For the trend we need total GMV/commission per day (not per‑locker)
    const chart3 = daily.map((d) => ({
      name: d.day,
      gmv: d.gmv || 0,
      commission: d.commission || 0,
    }));

    // determine whether the trend data contains any real variation; if not,
    // we will suppress the chart and just show n/a
    const hasTrendVariation = (() => {
      if (chart3.length < 2) return false;
      for (let i = 1; i < chart3.length; i++) {
        if (
          chart3[i].gmv !== chart3[i - 1].gmv ||
          chart3[i].commission !== chart3[i - 1].commission
        ) {
          return true;
        }
      }
      return false;
    })();

    // build accumulated series (gmv and commission) for daily totals
    const accumData = [];
    let cumGmv = 0,
      cumCom = 0;
    chart3.forEach((d) => {
      cumGmv += d.gmv || 0;
      cumCom += d.commission || 0;
      accumData.push({ name: d.name, gmv: cumGmv, commission: cumCom });
    });

    const hasAccumVariation = (() => {
      if (accumData.length < 2) return false;
      for (let i = 1; i < accumData.length; i++) {
        if (
          accumData[i].gmv !== accumData[i - 1].gmv ||
          accumData[i].commission !== accumData[i - 1].commission
        ) {
          return true;
        }
      }
      return false;
    })();

    // Build LY accumulated daily series aligned by day-index (position in period)
    // so we can overlay it on the same chart even though the year differs.
    // Uses the same filtered LY sales (lySalesFiltered) so customer/country/etc filters apply.
    const lyAccumData = (() => {
      if (!lySalesFiltered.length) return [];
      const byDay = {};
      lySalesFiltered.forEach((r) => {
        const day = r.Day
          ? r.Day.slice
            ? r.Day.slice(0, 10)
            : String(r.Day).slice(0, 10)
          : "unknown";
        if (!byDay[day]) byDay[day] = { gmv: 0, commission: 0 };
        byDay[day].gmv += Number(r.GMV_net || r.SalesTotal || 0) || 0;
        byDay[day].commission += Number(r.CommissionAmount || 0) || 0;
      });
      const days = Object.keys(byDay).sort();
      const acc = [];
      let cGmv = 0, cCom = 0;
      days.forEach((d) => {
        cGmv += byDay[d].gmv || 0;
        cCom += byDay[d].commission || 0;
        acc.push({ gmv: cGmv, commission: cCom });
      });
      return acc;
    })();

    // Merge LY values into accumData by position (index)
    const mergedAccumData = accumData.map((d, i) => ({
      ...d,
      lyGmv: lyAccumData[i] != null ? lyAccumData[i].gmv : null,
      lyCommission: lyAccumData[i] != null ? lyAccumData[i].commission : null,
    }));
    const hasLy = lyAccumData.length > 0;
    const { compareStartDate, compareEndDate, lyPersistedHasData } = this.state;
    const hasCompareRange = !!(compareStartDate && compareEndDate);

    // Local helper: render a delta row matching the main KPI card style
    const renderLockerDelta = (cur, ly) => {
      if (!hasCompareRange || !lyPersistedHasData || ly === null || ly === undefined || ly === 0) {
        return <div className="small text-muted">vs LY: n/a</div>;
      }
      const delta = cur - ly;
      const pctVal = ly !== 0 ? (delta / ly) * 100 : null;
      const colorClass = delta > 0 ? "text-success" : delta < 0 ? "text-danger" : "text-muted";
      const arrow = delta > 0 ? "▲" : delta < 0 ? "▼" : "";
      return (
        <div className="small">
          <span className="text-muted">vs LY:&nbsp;</span>
          <span className={colorClass}>
            {arrow} {delta >= 0 ? "+" : "-"}€{fmtCurrency(Math.abs(delta))}
            {pctVal !== null ? ` (${pctVal >= 0 ? "+" : ""}${pctVal.toFixed(1)}%)` : ""}
          </span>
        </div>
      );
    };

    // KPI card matching makeCard style from the main render IIFE
    const lockerKpiCard = (icon, label, value, cur, ly) => (
      <div
        className="dashboard-kpi-card"
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          padding: "10px 12px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <i className={`fas fa-${icon}`} style={{ fontSize: 12, color: "#6b7280" }} />
          <div className="kpi-label" style={{ margin: 0, fontSize: 12 }}>{label}</div>
        </div>
        <div style={{ marginTop: 8 }}>
          <div className="kpi-value" style={{ fontSize: 20, fontWeight: 700 }}>
            {value !== null && value !== undefined ? `${fmtCurrency(value)} €` : "—"}
          </div>
        </div>
        <div style={{ marginTop: 8 }}>
          {renderLockerDelta(cur, ly)}
        </div>
      </div>
    );

    return (
      <div className="dashboard-kpi-row mb-3" style={{ display: "flex", gap: 12, alignItems: "stretch", flexWrap: "wrap" }}>
        {/* Left column: two stacked KPI cards */}
        <div style={{ flex: "0 0 220px", minWidth: 180, display: "flex", flexDirection: "column", gap: 12 }}>
          {lockerKpiCard("boxes", "GMV por locker disponible", perLockerGMV, perLockerGMV, lyPerLockerGMV)}
          {lockerKpiCard("hand-holding-usd", "Comisión neta por locker disponible", perLockerCom, perLockerCom, lyPerLockerCom)}
        </div>
        {/* Right column: wide trend chart, fills remaining space and matches left column height */}
        <div style={{ flex: "1 1 400px", minWidth: 300, display: "flex", flexDirection: "column" }}>
          {this.renderLockerCard(
            "Tendencia acumulada GMV vs comisión",
            null,
            mergedAccumData,
            "line",
            "chart-area",
            null,
            { showLy: hasLy },
          )}
        </div>
      </div>
    );
  }

  renderGMVAndCommissionTrend() {
    const sales = this.aggregateSales().rows || [];

    // compute daily totals map
    const byDay = {};
    sales.forEach((r) => {
      const day = r.Day
        ? r.Day.slice
          ? r.Day.slice(0, 10)
          : String(r.Day).slice(0, 10)
        : "unknown";
      if (!byDay[day]) byDay[day] = { gmv: 0, commission: 0 };
      byDay[day].gmv += Number(r.GMV_net || r.SalesTotal || 0) || 0;
      byDay[day].commission += Number(r.CommissionAmount || 0) || 0;
    });

    const days = Object.keys(byDay).sort();
    if (!days.length)
      return (
        <div className="text-muted">
          No sales data for the selected period / filters
        </div>
      );

    const maxGMV = Math.max(...days.map((d) => byDay[d].gmv)) || 1;
    return (
      <div>
        <div style={{ display: "flex", gap: 8 }}>
          {days.map((d) => (
            <div key={d} style={{ flex: 1, textAlign: "center" }}>
              <div
                style={{
                  height: 100,
                  background: "#f5f7fb",
                  borderRadius: 4,
                  padding: 8,
                  display: "flex",
                  alignItems: "flex-end",
                  justifyContent: "center",
                }}
              >
                <div
                  style={{
                    width: "70%",
                    height: `${(byDay[d].gmv / maxGMV) * 100}%`,
                    background: "#2f6fdb",
                    borderRadius: 4,
                    position: "relative",
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      left: 4,
                      bottom: 2,
                      width: "60%",
                      height: `${Math.min(100, (byDay[d].commission / (byDay[d].gmv || 1)) * 100)}%`,
                      background: "#28a745",
                      borderRadius: 3,
                      opacity: 0.95,
                    }}
                  />
                </div>
              </div>
              <div style={{ marginTop: 6, fontSize: 11 }}>{d}</div>
              <div style={{ marginTop: 6, fontSize: 12, fontWeight: 600 }}>
                GMV {fmtCurrency(byDay[d].gmv)} € • Commission{" "}
                {fmtCurrency(byDay[d].commission)} €
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  renderInstalledDonuts() {
    const opRows = this.aggregateOperational().raw || [];
    const asOfCurrent = this.state.endDate
      ? new Date(this.state.endDate)
      : undefined;
    const byCountry = this.computeInstalledBy(opRows, "Country", asOfCurrent);
    const byCategory = this.computeInstalledBy(opRows, "Category", asOfCurrent); // preparado para 'vertical'
    const byBusiness = this.computeInstalledBy(
      opRows,
      "BusinessModel",
      asOfCurrent,
    );

    const { installedHidden } = this.state || {
      installedHidden: { country: [], category: [], businessModel: [] },
    };
    const isHidden = (group, key) =>
      installedHidden &&
      installedHidden[group] &&
      installedHidden[group].includes(key);
    const toggleInstalledSlice = (group, key) => {
      this.setState((prev) => {
        const prevArr =
          (prev.installedHidden && prev.installedHidden[group]) || [];
        const exists = prevArr.includes(key);
        const nextArr = exists
          ? prevArr.filter((k) => k !== key)
          : prevArr.concat([key]);
        return {
          installedHidden: {
            ...(prev.installedHidden || {}),
            [group]: nextArr,
          },
        };
      });
    };

    const colors = [
      "#2f6fdb",
      "#28a745",
      "#ffb020",
      "#ef4444",
      "#6f42c1",
      "#0ea5a0",
      "#7c3aed",
      "#f97316",
    ];

    const renderDonutCard = (title, data, groupKey) => {
      const itemsBase = (data.items || []).slice(0, 6);
      const other = Math.max(
        0,
        (data.total || 0) - itemsBase.reduce((s, i) => s + i.value, 0),
      );
      const items =
        other > 0
          ? [
              ...itemsBase,
              { key: "Other", value: other, pct: other / (data.total || 1) },
            ]
          : itemsBase;

      if (!items.length)
        return (
          <div className="dashboard-card-section" style={{ minWidth: 280 }}>
            <div style={{ fontWeight: 700 }}>{title}</div>
            <div className="text-muted small mt-2">No installation data</div>
          </div>
        );

      const chartData = items.map((i) => ({
        name: i.key,
        value: i.value,
        pct: i.pct,
      }));
      const visibleChartData = chartData.filter(
        (d) => !isHidden(groupKey, d.name),
      );
      const total = data.total || chartData.reduce((s, i) => s + i.value, 0);

      return (
        <div
          className="dashboard-card-section"
          style={{ minWidth: 320, flex: "1 1 320px" }}
        >
          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <div style={{ width: 180, height: 160 }}>
              <PieChart width={180} height={160}>
                {/* smaller hole + thicker stroke for better visibility */}
                <Pie
                  data={visibleChartData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={60}
                  paddingAngle={2}
                  label={({ percent }) => `${(percent * 100).toFixed(1)}%`}
                  labelLine={{
                    stroke: "#6b7280",
                    strokeDasharray: "3 4",
                    strokeWidth: 1,
                  }}
                  onClick={(entry) =>
                    toggleInstalledSlice(groupKey, entry.name)
                  }
                >
                  {visibleChartData.map((entry, idx) => (
                    <Cell
                      key={`cell-${groupKey}-${idx}`}
                      fill={colors[idx % colors.length]}
                    />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value, name) => [
                    `${value} (${total > 0 ? ((value / total) * 100).toFixed(1) : 0}%)`,
                    name,
                  ]}
                />
              </PieChart>
            </div>

            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700 }}>{title}</div>
              <div className="text-muted small">Total lockers: {total}</div>

              <div
                style={{
                  marginTop: 10,
                  display: "flex",
                  flexDirection: "column",
                  gap: 6,
                }}
              >
                {items.map((it, idx) => {
                  const hidden = isHidden(groupKey, it.key);
                  const displayPct =
                    it.pct !== undefined
                      ? (it.pct * 100).toFixed(1)
                      : total > 0
                        ? ((it.value / total) * 100).toFixed(1)
                        : "0.0";
                  return (
                    <div
                      key={it.key}
                      onClick={() => toggleInstalledSlice(groupKey, it.key)}
                      style={{
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        opacity: hidden ? 0.42 : 1,
                      }}
                    >
                      <div
                        style={{
                          width: 12,
                          height: 12,
                          background: colors[idx % colors.length],
                          borderRadius: 3,
                        }}
                      />
                      <div style={{ fontSize: 13, fontWeight: 700 }}>
                        {it.key}
                      </div>
                      <div
                        className="text-muted small"
                        style={{ marginLeft: 6 }}
                      >
                        {it.value} • {displayPct}%
                      </div>
                      <div
                        className="text-muted small"
                        style={{ marginLeft: "auto" }}
                      >
                        {hidden ? "hidden" : "visible"}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      );
    };

    return (
      <div
        style={{
          display: "flex",
          gap: 32, // extra space so percent labels have room
          alignItems: "flex-start",
          flexWrap: "wrap",
        }}
      >
        {renderDonutCard("Instaladas por país", byCountry, "country")}
        {renderDonutCard("Instaladas por vertical", byCategory, "category")}
        {renderDonutCard(
          "Instaladas por modelo negocio",
          byBusiness,
          "businessModel",
        )}
      </div>
    );
  }

  renderTopLocations() {
    // gather data from sales and operational rows — grouped BY CUSTOMER
    let salesRows = this.aggregateSales().rows || [];
    let opRows = this.aggregateOperational().raw || [];
    const { activeOnly } = this.state;

    // key = CustomerName (or Customer id as fallback)
    const customerKey = (r) =>
      (r.CustomerName || r.Customer || r.CustomerId || "Unknown") + "";

    const map = {};

    salesRows.forEach((r) => {
      const key = customerKey(r);
      if (!map[key]) {
        map[key] = {
          customer: r.CustomerName || r.Customer || key,
          country:
            r.CustomerCountry ||
            r.Country ||
            r.SiteCountry ||
            "",
          gmv: 0,
          commission: 0,
          transactions: 0,
          incidents: 0,
          // for occupancy: accumulate numerator (occupied) and denominator (available)
          totalOccupied: 0,
          totalAvailable: 0,
          // for availability: accumulate available vs total
          totalAvailLockers: 0,
          totalLockers: 0,
        };
      }
      map[key].gmv += Number(r.GMV_net || r.SalesTotal || 0) || 0;
      map[key].commission += Number(r.CommissionAmount || 0) || 0;
      map[key].transactions += Number(r.TotalTickets || 0) || 0;
      // fill country from first row that has it
      if (!map[key].country) {
        map[key].country =
          r.CustomerCountry || r.Country || r.SiteCountry || "";
      }
    });

    // Build Day|MachineSerial → TotalTickets lookup so we can fall back when
    // LockersOccupied is 0/absent in operational rows.
    const salesOccLookup = {};
    salesRows.forEach((r) => {
      const day = r.Day
        ? r.Day.slice
          ? r.Day.slice(0, 10)
          : String(r.Day).slice(0, 10)
        : "";
      const machine = (r.MachineSerial || r.MachineId || r.Serial || "") + "";
      const k = `${day}|${machine}`;
      salesOccLookup[k] =
        (salesOccLookup[k] || 0) + (Number(r.TotalTickets || 0) || 0);
    });

    opRows.forEach((r) => {
      const key = customerKey(r);
      if (!map[key]) {
        map[key] = {
          customer: r.CustomerName || r.Customer || key,
          country:
            r.CustomerCountry ||
            r.Country ||
            r.SiteCountry ||
            "",
          gmv: 0,
          commission: 0,
          transactions: 0,
          incidents: 0,
          totalOccupied: 0,
          totalAvailable: 0,
          totalAvailLockers: 0,
          totalLockers: 0,
        };
      }
      // fill country from first row that has it
      if (!map[key].country) {
        map[key].country =
          r.CustomerCountry || r.Country || r.SiteCountry || "";
      }

      const total =
        (r.TotlockerStatus && r.TotlockerStatus.total) || r.TotalLockers || 0;
      const fault = (r.TotlockerStatus && r.TotlockerStatus.fault) || 0;
      const available = total - fault;

      const opDay = r.Day
        ? r.Day.slice
          ? r.Day.slice(0, 10)
          : String(r.Day).slice(0, 10)
        : "";
      const opMachine = (r.MachineSerial || r.MachineId || r.Serial || "") + "";
      const lockersOccupied =
        Number(r.LockersOccupied || 0) ||
        salesOccLookup[`${opDay}|${opMachine}`] ||
        0;

      // Aggregate numerators and denominators for weighted occupancy and availability
      // Occupancy  = Σ lockersOccupied / Σ available
      // Availability = Σ available / Σ total
      map[key].totalOccupied += lockersOccupied;
      map[key].totalAvailable += available;
      map[key].totalAvailLockers += available;
      map[key].totalLockers += total;

      map[key].incidents += Number(r.IncidencesOpened || 0) || 0;
    });

    // build array and compute weighted ratios (not averages of per-row ratios)
    let arr = Object.values(map).map((s) => {
      const avgOcc =
        s.totalAvailable > 0 ? s.totalOccupied / s.totalAvailable : null;
      const avgAvail =
        s.totalLockers > 0 ? s.totalAvailLockers / s.totalLockers : null;
      return { ...s, avgOcc, avgAvail };
    });

    if (activeOnly) {
      arr = arr.filter(
        (s) => s.gmv > 0 || s.transactions > 0 || s.incidents > 0,
      );
    }

    const metrics = [
      { key: "gmv", label: "GMV" },
      { key: "transactions", label: "Transacciones" },
      { key: "commission", label: "Comisión neta" },
      { key: "incidents", label: "Incidencias" },
    ];
    const { topMetric } = this.state;

    arr.sort((a, b) => (b[topMetric] || 0) - (a[topMetric] || 0));
    const top5 = arr.slice(0, 5);

    // render tabs + table
    return (
      <div>
        <ul className="nav nav-tabs mb-2">
          {metrics.map((m) => (
            <li className="nav-item" key={m.key}>
              <button
                className={`nav-link ${topMetric === m.key ? "active" : ""}`}
                onClick={() => this.setState({ topMetric: m.key })}
                type="button"
              >
                {m.label}
              </button>
            </li>
          ))}
        </ul>
        <div className="table-responsive">
          <table className="table table-sm">
            <thead>
              <tr>
                <th>#</th>
                <th>Cliente</th>
                <th>País</th>
                <th>GMV</th>
                <th>Comisión</th>
                <th>Tx</th>
                <th>Ocupación</th>
                <th>Disponibilidad</th>
                <th>Incidencias</th>
              </tr>
            </thead>
            <tbody>
              {top5.map((s, idx) => (
                <tr key={s.customer}>
                  <td>{idx + 1}</td>
                  <td>{s.customer}</td>
                  <td>{s.country}</td>
                  <td>{fmtCurrency(s.gmv)}</td>
                  <td>{fmtCurrency(s.commission)}</td>
                  <td>{s.transactions}</td>
                  <td>{s.avgOcc !== null ? pct(s.avgOcc) : "—"}</td>
                  <td>{s.avgAvail !== null ? pct(s.avgAvail) : "—"}</td>
                  <td>{s.incidents}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // % máquinas reportando hoy
  computeReportingQuality() {
    const today = new Date();
    const twelveHoursAgo = new Date(today.getTime() - 12 * 60 * 60 * 1000);
    const { country, businessModel, vertical, siteFilter } = this.state;
    const EXCLUDE_CID = "620d1664db950220fabc0a41"; // VACWAY TECH
    let arr = Array.isArray(this.props.generatedOperationalArqueos)
      ? this.props.generatedOperationalArqueos
      : [];
    // Exclude VACWAY TECH
    arr = arr.filter((r) => ((r.CustomerId || r.Customer || "") + "") !== EXCLUDE_CID);
    // Only installed machines (same logic as donuts)
    arr = arr.filter(
      (r) =>
        (!r.installed_at || new Date(r.installed_at) <= today) &&
        (!r.removed_at || new Date(r.removed_at) > today),
    );
    // Apply dashboard filters
    if (businessModel) {
      const map = { revenue_share: "REVENUE SHARE", alquiler: "RENTAL", venta: "SALE" };
      const bm = map[businessModel] || businessModel;
      arr = arr.filter((r) => r.BusinessModel === bm);
    }
    if (country && country.trim()) {
      const q = country.trim().toLowerCase();
      arr = arr.filter((r) =>
        (r.Country || r.CustomerCountry || r.SiteCountry || "").toString().toLowerCase().includes(q),
      );
    }
    if (vertical && vertical.trim()) {
      const q = vertical.trim().toLowerCase();
      arr = arr.filter((r) =>
        ((r.Category || r.Vertical || r.CategoryName || "") + "").toString().toLowerCase().includes(q),
      );
    }
    if (siteFilter && siteFilter.trim()) {
      const q = siteFilter.trim();
      arr = arr.filter((r) => this.rowMatchesFilter(r, q));
    }
    const uniqueMachines = {};
    arr.forEach((r) => {
      const machineId =
        r.MachineSerial || r.MachineId || r.Serial || r._id || "unknown";
      if (!uniqueMachines[machineId]) uniqueMachines[machineId] = r;
    });
    const total = Object.keys(uniqueMachines).length;
    const reporting = Object.values(uniqueMachines).filter((r) => {
      if (!r.last_seen_at) return false;
      const lastSeen = new Date(r.last_seen_at);
      return lastSeen >= twelveHoursAgo;
    }).length;
    return { reporting, total, pct: total > 0 ? reporting / total : 0 };
  }

  renderReportingQuality() {
    const { reporting, total, pct } = this.computeReportingQuality();
    let color = pct >= 0.85 ? "#059669" : pct >= 0.7 ? "#F59E0B" : "#EF4444";
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          fontWeight: 600,
        }}
      >
        <i
          className="fas fa-broadcast-tower"
          style={{ color, fontSize: 16 }}
        ></i>
        <span>
          % máquinas reportando hoy:{" "}
          <span style={{ color }}>{(pct * 100).toFixed(0)}%</span>
          <span style={{ fontWeight: 400, marginLeft: 8, fontSize: 13 }}>
            ({reporting}/{total})
          </span>
        </span>
      </div>
    );
  }

  render() {
    const { error, role } = this.props;
    const {
      loading,
      startDate,
      endDate,
      compareStartDate,
      compareEndDate,
      country,
      businessModel,
      vertical,
      activeOnly,
      siteFilter,
      showAdvancedFilters,
      periodType,
      tempPeriodType,
      tempStartDate,
      tempEndDate,
    } = this.state;

    // derive list of customer names only; ignore coordinates entirely
    const customerOptions = (() => {
      let arr = Array.isArray(this.props.generatedOperationalArqueos)
        ? this.props.generatedOperationalArqueos.slice()
        : [];
      if (businessModel) {
        const map = {
          revenue_share: "REVENUE SHARE",
          alquiler: "RENTAL",
          venta: "SALE",
        };
        const bm = map[businessModel] || businessModel;
        arr = arr.filter((r) => r.BusinessModel === bm);
      }
      if (country && country.trim()) {
        const q = country.trim().toLowerCase();
        arr = arr.filter((r) =>
          (r.Country || r.CustomerCountry || r.SiteCountry || "")
            .toString()
            .toLowerCase()
            .includes(q),
        );
      }
      const names = arr
        .map((r) => r.CustomerName || "")
        .filter((v) => v && v.toString().trim())
        .map((v) => v.toString());
      return [...new Set(names)].sort();
    })();
    // determine whether there's enough input to fetch/cargar
    const canLoad = (() => {
      if (!tempStartDate) return false;
      switch (tempPeriodType) {
        case "range":
          return !!tempEndDate;
        case "day":
        case "month":
        case "year":
          return true;
        default:
          return false;
      }
    })();

    // compute default period values
    const today = new Date();
    const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    const defaultStart = localIso(monthStart);
    const defaultEnd = localIso(today);

    // compute text to show in header
    const displayCurrentStart = startDate || defaultStart;
    const displayCurrentEnd = endDate || defaultEnd;

    // show comparison only if the user supplied both dates; otherwise leave
    // blank so we can render "(no comparison)" and avoid confusing LY data
    let displayCompareStart = compareStartDate;
    let displayCompareEnd = compareEndDate;
    // note: we no longer auto-populate the compare dates with last year
    // when the inputs are empty.  explicitly entering a range is required.

    const formatTitle = (start, end, type) => {
      if (!start) return "";
      switch (type) {
        case "range":
          return `${start} → ${end}`;
        case "day":
          return start;
        case "month":
          return start.slice(0, 7); // YYYY-MM
        case "year":
          return start.slice(0, 4);
        default:
          return `${start} → ${end}`;
      }
    };

    let periodTitle = formatTitle(
      displayCurrentStart,
      displayCurrentEnd,
      periodType,
    );
    if (!periodTitle) periodTitle = "(sin periodo)"; // no dates entered yet
    const hasCompareData = !!this.state.lyPersistedHasData;
    let compareTitleRaw = formatTitle(
      displayCompareStart,
      displayCompareEnd,
      periodType,
    );
    const compareTitle = compareTitleRaw
      ? hasCompareData
        ? compareTitleRaw
        : `${compareTitleRaw} (no data)`
      : "(no comparison)";

    // helper to know if comparison is exactly last year same period
    let isLyComparison = false;
    if (
      displayCurrentStart &&
      displayCurrentEnd &&
      displayCompareStart &&
      displayCompareEnd
    ) {
      const cs = new Date(displayCurrentStart);
      const ce = new Date(displayCurrentEnd);
      const lycs = new Date(cs);
      lycs.setFullYear(lycs.getFullYear() - 1);
      const lyce = new Date(ce);
      lyce.setFullYear(lyce.getFullYear() - 1);
      isLyComparison =
        displayCompareStart === localIso(lycs) &&
        displayCompareEnd === localIso(lyce);
    }
    // if there is no compare range at all, we will never treat it as LY
    // comparison (isLyComparison stays false)

    // prepare installed snapshot aggregations used by the three donut cards
    // `aggregateOperational()` already applies the active-only filter when
    // `this.state.activeOnly` is true, so the rows we work with here reflect
    // the current toggle state.
    // Installed base is stock — not period-dependent (spec §7).
    // Use ALL operational arqueos rows so the donuts always reflect the
    // real machine inventory regardless of the selected date range.
    // Non-period filters (country, BM, vertical) are still applied via
    // aggregateOperational({ ignoreActive: true }).
    let _opRows_forInstalled = this.aggregateOperational({ ignoreActive: true }).raw || [];

    // When "Solo ubicaciones activas" is on, restrict the donuts to machines
    // that had actual sales (TotalTickets > 0 or GMV > 0) in the selected
    // period.  We derive the active set from the SALES arqueos because
    // operational rows do not carry transaction counts.
    const _activeSalesRows = this.aggregateSales().rows || [];
    const _activeMachineIds = activeOnly
      ? new Set(
          _activeSalesRows
            .filter(
              (r) =>
                (Number(r.TotalTickets || 0) || 0) > 0 ||
                (Number(r.GMV_net || r.SalesTotal || 0) || 0) > 0,
            )
            .map((r) => (r.MachineSerial || r.MachineId || r.Serial || "") + "")
            .filter(Boolean),
        )
      : null; // null = no restriction

    // Separate active-machine set used ONLY for populating dropdown options.
    // Must NOT apply country/vertical/siteFilter so that selecting one filter
    // never collapses the options of another filter (they stay independent).
    // Only BM and period (already committed via startDate/endDate on sales rows) are applied.
    const _activeMachineIds_forOptions = (() => {
      if (!activeOnly) return null;
      const EXCL = "620d1664db950220fabc0a41";
      let arr = Array.isArray(this.props.generatedSalesArqueos)
        ? this.props.generatedSalesArqueos.slice()
        : [];
      arr = arr.filter((r) => ((r.CustomerId || r.Customer || "") + "") !== EXCL);
      if (startDate || endDate) {
        arr = arr.filter((r) => {
          const day = r.Day ? (r.Day.slice ? r.Day.slice(0, 10) : String(r.Day).slice(0, 10)) : null;
          if (!day) return false;
          if (startDate && day < startDate) return false;
          if (endDate && day > endDate) return false;
          return true;
        });
      }
      if (businessModel) {
        const bmMap2 = { revenue_share: "REVENUE SHARE", alquiler: "RENTAL", venta: "SALE" };
        const bm = bmMap2[businessModel] || businessModel;
        arr = arr.filter((r) => r.BusinessModel === bm);
      }
      return new Set(
        arr
          .filter((r) => (Number(r.TotalTickets || 0) || 0) > 0 || (Number(r.GMV_net || r.SalesTotal || 0) || 0) > 0)
          .map((r) => (r.MachineSerial || r.MachineId || r.Serial || "") + "")
          .filter(Boolean),
      );
    })();

    if (_activeMachineIds) {
      _opRows_forInstalled = _opRows_forInstalled.filter((r) => {
        const mid = (r.MachineSerial || r.MachineId || r.Serial || "") + "";
        return _activeMachineIds.has(mid);
      });
    }

    // we also need the list of *all* countries available prior to applying the
    // country filter (otherwise selecting one country leaves the select with
    // a single option). build a parallel set of rows that omit the country
    // filter but respect the other toggles.
    // Uses _activeMachineIds_forOptions (no cross-filters) so country options
    // are never restricted by the vertical selection and vice-versa.
    const _allCountryRows_forInstalled = (() => {
      const { businessModel } = this.state;
      let arr = Array.isArray(this.props.generatedOperationalArqueos)
        ? this.props.generatedOperationalArqueos
        : [];
      if (businessModel) {
        const map = {
          revenue_share: "REVENUE SHARE",
          alquiler: "RENTAL",
          venta: "SALE",
        };
        const bm = map[businessModel] || businessModel;
        arr = arr.filter((r) => r.BusinessModel === bm);
      }
      if (activeOnly && _activeMachineIds_forOptions) {
        arr = arr.filter((r) => {
          const mid = (r.MachineSerial || r.MachineId || r.Serial || "") + "";
          return _activeMachineIds_forOptions.has(mid);
        });
      }
      return arr;
    })();

    // similarly prepare a list of all category rows (ignore the client-side
    // `country` filter) so the vertical select doesn't collapse when a
    // category is chosen.
    // Uses _activeMachineIds_forOptions (no cross-filters) so vertical options
    // are never restricted by the country selection.
    const _allCategoryRows_forInstalled = (() => {
      const { businessModel } = this.state;
      let arr = Array.isArray(this.props.generatedOperationalArqueos)
        ? this.props.generatedOperationalArqueos
        : [];
      if (businessModel) {
        const map = {
          revenue_share: "REVENUE SHARE",
          alquiler: "RENTAL",
          venta: "SALE",
        };
        const bm = map[businessModel] || businessModel;
        arr = arr.filter((r) => r.BusinessModel === bm);
      }
      if (activeOnly && _activeMachineIds_forOptions) {
        arr = arr.filter((r) => {
          const mid = (r.MachineSerial || r.MachineId || r.Serial || "") + "";
          return _activeMachineIds_forOptions.has(mid);
        });
      }
      return arr;
    })();

    const currentAsOf = this.state.endDate
      ? new Date(this.state.endDate)
      : undefined;
    const _byCountry_forInstalled = this.computeInstalledBy(
      _opRows_forInstalled,
      "Country",
      currentAsOf,
    );
    const _byCategory_forInstalled = this.computeInstalledBy(
      _opRows_forInstalled,
      "Category",
      currentAsOf,
    );
    const _byBusiness_forInstalled = this.computeInstalledBy(
      _opRows_forInstalled,
      "BusinessModel",
      currentAsOf,
    );

    // aggregate the ‘all countries’ rows as well for dropdown options
    const _allCountries_forInstalled = this.computeInstalledBy(
      _allCountryRows_forInstalled,
      "Country",
      currentAsOf,
    );

    // also prepare comparison aggregates if LY data is present
    // start with LY operational rows, but trim to the comparison end
    // Spec: installed base is stock — not period-dependent.
    // When activeOnly is OFF both current and comparison donuts must use the
    // same source (current stock) so the totals are identical.
    // When activeOnly is ON the comparison uses the LY sales-active machines
    // (legitimately different from the current active set).
    //
    // Build active machine set for the COMPARISON period from LY sales rows.
    // Only machines with actual LY sales are kept when activeOnly is on.
    const _lyActiveMachineIds = this.state.activeOnly
      ? new Set(
          (Array.isArray(this.state.lySalesArqueos) ? this.state.lySalesArqueos : [])
            .filter(
              (r) =>
                (Number(r.TotalTickets || 0) || 0) > 0 ||
                (Number(r.GMV_net || r.SalesTotal || 0) || 0) > 0,
            )
            .map((r) => (r.MachineSerial || r.MachineId || r.Serial || "") + "")
            .filter(Boolean),
        )
      : null;

    // Source for comparison donuts:
    // • activeOnly OFF → reuse _opRows_forInstalled (same stock, same totals)
    // • activeOnly ON  → use LY operational rows filtered to LY active machines
    let _filteredCompareRows;
    if (!this.state.activeOnly) {
      // stock is the same regardless of comparison period
      _filteredCompareRows = _opRows_forInstalled.slice();
    } else {
      // fetch LY op rows (LY active machines only)
      const _compareOpRows_forInstalled = Array.isArray(this.state.lyOperationalArqueos)
        ? this.state.lyOperationalArqueos
        : [];
      _filteredCompareRows = _lyActiveMachineIds
        ? _compareOpRows_forInstalled.filter((r) => {
            const mid = (r.MachineSerial || r.MachineId || r.Serial || "") + "";
            return _lyActiveMachineIds.has(mid);
          })
        : _compareOpRows_forInstalled.slice();
    }
    // also apply business model filter client-side like in aggregate methods
    if (businessModel) {
      const map = {
        revenue_share: "REVENUE SHARE",
        alquiler: "RENTAL",
        venta: "SALE",
      };
      const bm = map[businessModel] || businessModel;
      _filteredCompareRows = _filteredCompareRows.filter(
        (r) => r.BusinessModel === bm,
      );
    }
    // also apply country filter to comparison rows
    if (country && country.trim()) {
      const q = country.trim().toLowerCase();
      _filteredCompareRows = _filteredCompareRows.filter((r) =>
        (r.Country || r.CustomerCountry || r.SiteCountry || "")
          .toString()
          .toLowerCase()
          .includes(q),
      );
    }
    // also apply siteFilter to comparison rows
    if (siteFilter && siteFilter.trim()) {
      const q = siteFilter.trim();
      _filteredCompareRows = _filteredCompareRows.filter((r) => this.rowMatchesFilter(r, q));
    }
    // also apply vertical filter to comparison rows
    if (vertical && vertical.trim()) {
      const q = vertical.trim().toLowerCase();
      _filteredCompareRows = _filteredCompareRows.filter((r) =>
        ((r.Category || r.Vertical || r.CategoryName || "") + "")
          .toString()
          .toLowerCase()
          .includes(q),
      );
    }
    // When activeOnly is OFF both donuts use the same source rows AND the same
    // asOf date so that installed_at/removed_at filtering produces identical
    // results (identical totals in current and comparison donuts).
    const compareAsOf = !this.state.activeOnly
      ? currentAsOf
      : (this.state.compareEndDate ? new Date(this.state.compareEndDate) : undefined);
    const _byCountry_forInstalledCompare = this.computeInstalledBy(
      _filteredCompareRows,
      "Country",
      compareAsOf,
    );
    const _byCategory_forInstalledCompare = this.computeInstalledBy(
      _filteredCompareRows,
      "Category",
      compareAsOf,
    );
    const _byBusiness_forInstalledCompare = this.computeInstalledBy(
      _filteredCompareRows,
      "BusinessModel",
      compareAsOf,
    );

    // also build the full list of comparison countries (ignoring the current
    // country filter) for the select box.
    // When activeOnly is OFF reuse the current stock rows (same source as donuts).
    // When activeOnly is ON use the LY active machines without country filter.
    const _allCompareRows_forInstalled = !this.state.activeOnly
      ? _allCountryRows_forInstalled.slice()
      : (() => {
          let arr = _lyActiveMachineIds
            ? (Array.isArray(this.state.lyOperationalArqueos)
                ? this.state.lyOperationalArqueos
                : []
              ).filter((r) => {
                const mid = (r.MachineSerial || r.MachineId || r.Serial || "") + "";
                return _lyActiveMachineIds.has(mid);
              })
            : (Array.isArray(this.state.lyOperationalArqueos)
                ? this.state.lyOperationalArqueos.slice()
                : []);
          if (businessModel) {
            const bmMap = {
              revenue_share: "REVENUE SHARE",
              alquiler: "RENTAL",
              venta: "SALE",
            };
            const bm = bmMap[businessModel] || businessModel;
            arr = arr.filter((r) => r.BusinessModel === bm);
          }
          if (siteFilter && siteFilter.trim()) {
            const q = siteFilter.trim();
            arr = arr.filter((r) => this.rowMatchesFilter(r, q));
          }
          return arr;
        })();
    const _allCountries_forInstalledCompare = this.computeInstalledBy(
      _allCompareRows_forInstalled,
      "Country",
      compareAsOf,
    );

    // also compute full category lists for the compare period
    const _allCategories_forInstalledCompare = this.computeInstalledBy(
      _allCompareRows_forInstalled,
      "Category",
      compareAsOf,
    );

    // compute current-period full category list
    const _allCategories_forInstalled = this.computeInstalledBy(
      _allCategoryRows_forInstalled,
      "Category",
      currentAsOf,
    );

    // Compute the total installed across all available operational arqueos
    // BEFORE applying filters: dedupe by machine id (keep latest snapshot)
    // so the same machine present on multiple days is counted only once.
    const _allGenOpRows = Array.isArray(this.props.generatedOperationalArqueos)
      ? this.props.generatedOperationalArqueos.slice()
      : [];
    const _dedupedAllGenOp = this.dedupeLatestPerMachine(_allGenOpRows, currentAsOf);
    const _totalInstalledAll = _dedupedAllGenOp.reduce((s, r) => {
      const total = (r.TotlockerStatus && r.TotlockerStatus.total) || r.TotalLockers || 0;
      return s + (total || 0);
    }, 0);

    // Also compute the total installed after applying current filters
    // (deduped per machine so multiple-day snapshots don't double-count).
    const _dedupedFilteredOp = this.dedupeLatestPerMachine(_opRows_forInstalled, currentAsOf);
    const _totalInstalledFiltered = _dedupedFilteredOp.reduce((s, r) => {
      const total = (r.TotlockerStatus && r.TotlockerStatus.total) || r.TotalLockers || 0;
      return s + (total || 0);
    }, 0);

    return (
      <div className="dashboard-view">
        {/* Header (reuse FaultsView styles) */}
        <div
          className="dashboard-header"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div className="dashboard-title-section">
            <h1 className="dashboard-title">
              <i className="fas fa-chart-line"></i> Dashboard Operativo {
                ((this.props.role || '').toString().toUpperCase() === 'ADMIN') ? 'Admin' : 'Client'
              }
            </h1>
            <div className="small text-muted mt-1">
              Período: {periodTitle} (vs {compareTitle})
            </div>
          </div>

          <div className="dashboard-actions">
            <div
              className="d-flex align-items-center flex-wrap"
              style={{ gap: 8 }}
            >
              {role === 'ADMIN' && (<>
              <input
                type="text"
                list="customer-options"
                className="dashboard-filter-input"
                placeholder="Filtrar por cliente"
                ref={this.siteInputRef}
                defaultValue=""
                onChange={(e) => {
                  const val = e.target.value;
                  clearTimeout(this._siteFilterTimer);
                  this._siteFilterTimer = setTimeout(() => {
                    this.setState({ siteFilter: val });
                  }, 350);
                }}
                onBlur={(e) => {
                  clearTimeout(this._siteFilterTimer);
                  const val = e.target.value;
                  if (val === "" || customerOptions.includes(val)) {
                    this.setState({ siteFilter: val });
                  } else {
                    // revert displayed text to last committed value
                    if (this.siteInputRef.current) {
                      this.siteInputRef.current.value = this.state.siteFilter;
                    }
                  }
                }}
                style={{ width: 260 }}
              />
              <datalist id="customer-options">
                {customerOptions.map((c) => (
                  <option key={c} value={c} />
                ))}
              </datalist>
              {/* country filter dropdown - options driven by the installed donuts */}
              <select
                className="dashboard-filter-input"
                value={country}
                onChange={(e) => this.setState({ country: e.target.value })}
              >
                <option value="">País (todos)</option>
                {(() => {
                  // derive options from the lists that ignore the active country
                  // filter so that changing the selection doesn't collapse the
                  // menu to a single entry.
                  const base = Array.isArray(_allCountries_forInstalled.items)
                    ? _allCountries_forInstalled.items.map((i) => i.key)
                    : [];
                  let comp = [];
                  if (
                    hasCompareData &&
                    Array.isArray(_allCountries_forInstalledCompare.items)
                  ) {
                    comp = _allCountries_forInstalledCompare.items.map(
                      (i) => i.key,
                    );
                  }
                  const all = [...new Set([...base, ...comp])];
                  return all.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ));
                })()}
              </select>
              <select
                className="dashboard-filter-input"
                value={this.state.vertical}
                onChange={(e) => this.setState({ vertical: e.target.value })}
              >
                <option value="">Vertical (todos)</option>
                {(() => {
                  const base = Array.isArray(_allCategories_forInstalled.items)
                    ? _allCategories_forInstalled.items.map((i) => i.key)
                    : [];
                  let comp = [];
                  if (
                    hasCompareData &&
                    Array.isArray(_allCategories_forInstalledCompare.items)
                  ) {
                    comp = _allCategories_forInstalledCompare.items.map((i) => i.key);
                  }
                  const all = [...new Set([...base, ...comp])];
                  return all.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ));
                })()}
              </select>
              <select
                className="dashboard-filter-input"
                value={businessModel}
                onChange={(e) =>
                  this.setState({ businessModel: e.target.value })
                }
              >
                <option value="">Modelo negocio (todos)</option>
                <option value="revenue_share">Revenue-share</option>
                <option value="alquiler">Alquiler (Rental)</option>
                <option value="venta">Venta (Sale)</option>
              </select>
              </>)}
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <label className="small text-muted" style={{ margin: 0 }}>
                  Solo ubicaciones activas
                </label>
                <label className="dashboard-switch" style={{ margin: 0 }}>
                  <input
                    type="checkbox"
                    checked={activeOnly}
                    onChange={(e) =>
                      this.setState({ activeOnly: e.target.checked })
                    }
                  />
                  <span className="slider round" />
                </label>
              </div>
            </div>
            {/* toggle button floated right with indicator */}
            <button
              type="button"
              className="dashboard-filter-input toggle-filters"
              onClick={() =>
                this.setState({ showAdvancedFilters: !showAdvancedFilters })
              }
              style={{ display: "inline-flex", alignItems: "center", gap: 6, cursor: 'pointer' }}
            >
              {showAdvancedFilters ? (
                <>
                  <i className="fas fa-chevron-up" style={{ marginRight: 6 }}></i>
                  Ocultar filtros
                </>
              ) : (
                <>
                  <i className="fas fa-chevron-down" style={{ marginRight: 6 }}></i>
                  Mostrar filtros
                </>
              )}
            </button>
          </div>
        </div>
        {showAdvancedFilters && (
          <div
            className="dashboard-header mb-3"
            style={{ padding: "12px 20px", justifyContent: "center" }}
          >
            <div
              className="d-flex align-items-center flex-wrap justify-content-center"
              style={{ gap: 8 }}
            >
              {/* period type selector */}
              <select
                className="filter-input"
                value={this.state.tempPeriodType}
                onChange={(e) =>
                  this.setState({
                    tempPeriodType: e.target.value,
                    tempStartDate: "",
                    tempEndDate: "",
                    tempCompareStartDate: "",
                    tempCompareEndDate: "",
                  })
                }
                style={{ width: 140 }}
              >
                <option value="range">Rango</option>
                <option value="day">Día</option>
                <option value="month">Mes</option>
                <option value="year">Año</option>
              </select>

              {/* origin inputs, change based on period type */}
              {this.state.tempPeriodType === "range" && (
                <>
                  <input
                    type="date"
                    className="filter-input"
                    title="Origen inicio"
                    value={this.state.tempStartDate}
                    onChange={(e) =>
                      this.setState({ tempStartDate: e.target.value })
                    }
                    style={{ width: 140 }}
                  />
                  <input
                    type="date"
                    className="filter-input"
                    title="Origen fin"
                    value={this.state.tempEndDate}
                    onChange={(e) =>
                      this.setState({ tempEndDate: e.target.value })
                    }
                    style={{ width: 140 }}
                  />
                </>
              )}
              {this.state.tempPeriodType === "day" && (
                <input
                  type="date"
                  className="filter-input"
                  title="Origen (día)"
                  value={this.state.tempStartDate}
                  onChange={(e) =>
                    this.setState({ tempStartDate: e.target.value })
                  }
                  style={{ width: 140 }}
                />
              )}
              {this.state.tempPeriodType === "month" && (
                <input
                  type="month"
                  className="filter-input"
                  title="Origen (mes)"
                  value={this.state.tempStartDate}
                  onChange={(e) =>
                    this.setState({ tempStartDate: e.target.value })
                  }
                  style={{ width: 140 }}
                />
              )}
              {this.state.tempPeriodType === "year" && (
                <input
                  type="number"
                  min="1900"
                  max="2100"
                  className="filter-input"
                  title="Origen (año)"
                  value={this.state.tempStartDate}
                  onChange={(e) =>
                    this.setState({ tempStartDate: e.target.value })
                  }
                  style={{ width: 80 }}
                />
              )}

              <span
                className="small text-muted"
                style={{ display: "flex", alignItems: "center", gap: 4 }}
              >
                comp. <em>(leave blank to disable)</em>
                <button
                  className="btn btn-sm btn-secondary"
                  style={{
                    padding: "0 8px",
                    fontSize: "0.8rem",
                    height: "36px",
                    lineHeight: "36px",
                  }}
                  title="Autofill comparación mismo período año anterior"
                  onClick={this.fillLastYear}
                  disabled={!this.state.tempStartDate}
                >
                  Año ant.
                </button>
              </span>

              {/* compare inputs */}
              {this.state.tempPeriodType === "range" && (
                <>
                  <input
                    type="date"
                    className="filter-input"
                    title="Comparación inicio (dejar vacío para desactivar)"
                    value={this.state.tempCompareStartDate}
                    onChange={(e) =>
                      this.setState({ tempCompareStartDate: e.target.value })
                    }
                    style={{ width: 140 }}
                  />
                  <input
                    type="date"
                    className="filter-input"
                    title="Comparación fin (dejar vacío para desactivar)"
                    value={this.state.tempCompareEndDate}
                    onChange={(e) =>
                      this.setState({ tempCompareEndDate: e.target.value })
                    }
                    style={{ width: 140 }}
                  />
                </>
              )}
              {this.state.tempPeriodType === "day" && (
                <input
                  type="date"
                  className="filter-input"
                  title="Comparación (día)"
                  value={this.state.tempCompareStartDate}
                  onChange={(e) =>
                    this.setState({ tempCompareStartDate: e.target.value })
                  }
                  style={{ width: 140 }}
                />
              )}
              {this.state.tempPeriodType === "month" && (
                <input
                  type="month"
                  className="filter-input"
                  title="Comparación (mes)"
                  value={this.state.tempCompareStartDate}
                  onChange={(e) =>
                    this.setState({ tempCompareStartDate: e.target.value })
                  }
                  style={{ width: 140 }}
                />
              )}
              {this.state.tempPeriodType === "year" && (
                <input
                  type="number"
                  min="1900"
                  max="2100"
                  className="filter-input"
                  title="Comparación (año)"
                  value={this.state.tempCompareStartDate}
                  onChange={(e) =>
                    this.setState({ tempCompareStartDate: e.target.value })
                  }
                  style={{ width: 80 }}
                />
              )}

              <button
                className="btn btn-vw ml-2"
                onClick={async () => {
                  // translate temp inputs according to period type
                  const {
                    tempPeriodType,
                    tempStartDate,
                    tempEndDate,
                    tempCompareStartDate,
                    tempCompareEndDate,
                  } = this.state;

                  const normalize = (type, val1, val2) => {
                    let s = "";
                    let e = "";
                    if (!val1) return { s: "", e: "" };
                    switch (type) {
                      case "range":
                        s = val1;
                        e = val2;
                        break;
                      case "day":
                        s = val1;
                        e = val1;
                        break;
                      case "month": {
                        // val1 like YYYY-MM
                        const [y, m] = val1.split("-");
                        if (y && m) {
                          s = `${y}-${m}-01`;
                          const last = localIso(new Date(y, m, 0));
                          e = last;
                        }
                        break;
                      }
                      case "year": {
                        const y = val1;
                        if (y) {
                          s = `${y}-01-01`;
                          e = `${y}-12-31`;
                        }
                        break;
                      }
                    }
                    return { s, e };
                  };

                  const { s: startDate, e: endDate } = normalize(
                    tempPeriodType,
                    tempStartDate,
                    tempEndDate,
                  );
                  const { s: compareStartDate, e: compareEndDate } = normalize(
                    tempPeriodType,
                    tempCompareStartDate,
                    tempCompareEndDate,
                  );

                  await this.setState({
                    startDate,
                    endDate,
                    compareStartDate,
                    compareEndDate,
                    periodType: tempPeriodType,
                  });
                  this.load();
                }}
                disabled={loading || !canLoad}
              >
                {loading ? "Loading..." : "Cargar"}
              </button>
            </div>
          </div>
        )}

        {/* KPI cards row (styled via dashboard-kpi-row for consistent admin look) */}
        {error && <ErrorMessage message={error.message} />}

        {loading ? (
          <LoadingProgress />
        ) : (
          <div className="dashboard-kpi-wrapper pb-3">
            <div className="dashboard-kpi-row mb-3">
              {(() => {
                const current = this.aggregateSales();
                const op = this.aggregateOperational();
                let lySales = Array.isArray(this.state.lySalesArqueos)
                  ? this.state.lySalesArqueos.slice()
                  : [];
                let lyOpRows = Array.isArray(this.state.lyOperationalArqueos)
                  ? // restrict LY operational rows to comparison end date as well
                    this.state.lyOperationalArqueos.filter((r) => {
                      if (this.state.compareEndDate && r.Day) {
                        const day = new Date(
                          r.Day.slice ? r.Day.slice(0, 10) : r.Day,
                        );
                        return day <= new Date(this.state.compareEndDate);
                      }
                      return true;
                    })
                  : [];
                // apply same bm mapping as above
                if (businessModel) {
                  const map = {
                    revenue_share: "REVENUE SHARE",
                    alquiler: "RENTAL",
                    venta: "SALE",
                  };
                  const bm = map[businessModel] || businessModel;
                  lySales = lySales.filter((r) => r.BusinessModel === bm);
                  lyOpRows = lyOpRows.filter((r) => r.BusinessModel === bm);
                }
                // apply country filter to LY arrays too
                if (country && country.trim()) {
                  const q = country.trim().toLowerCase();
                  lySales = lySales.filter((r) =>
                    (r.CustomerCountry || r.Country || r.SiteCountry || "")
                      .toString()
                      .toLowerCase()
                      .includes(q),
                  );
                  lyOpRows = lyOpRows.filter((r) =>
                    (r.Country || r.CustomerCountry || r.SiteCountry || "")
                      .toString()
                      .toLowerCase()
                      .includes(q),
                  );
                }
                // apply vertical filter to LY arrays too
                if (vertical && vertical.trim()) {
                  const q = vertical.trim().toLowerCase();
                  // operational rows carry Category directly
                  lyOpRows = lyOpRows.filter((r) =>
                    ((r.Category || r.Vertical || r.CategoryName || "") + "")
                      .toString()
                      .toLowerCase()
                      .includes(q),
                  );
                  // sales rows don't carry Category — build machine→category map from filtered LY op rows
                  const lyMachineCatMap = {};
                  lyOpRows.forEach((r) => {
                    const mid = (r.MachineSerial || r.MachineId || r.Serial || "") + "";
                    if (mid && (r.Category || r.Vertical || r.CategoryName)) {
                      lyMachineCatMap[mid] = ((r.Category || r.Vertical || r.CategoryName) + "").toLowerCase();
                    }
                  });
                  lySales = lySales.filter((r) => {
                    const own = ((r.Category || r.Vertical || r.CategoryName || "") + "").toLowerCase();
                    if (own.includes(q)) return true;
                    const mid = (r.MachineSerial || r.MachineId || r.Serial || "") + "";
                    return (lyMachineCatMap[mid] || "").includes(q);
                  });
                }
                const lyAgg = this.aggregateFromRows(lySales);
                // Pass LY sales rows so the LockersOccupied fallback works for LY occupancy too
                const lyOpAgg = this.aggregateOperationalFromRows(lyOpRows, lySales);

                const roleLabel = (this.props.role || "").toString().toUpperCase();

                const renderDelta = (curNum, lyNum, opts = {}) => {
                  // opts: {type: 'currency'|'percent'|'points'|'count'}
                  // if there was no persisted comparison data or the LY value is
                  // missing/zero we can't compute a meaningful delta – always show
                  // n/a instead of pretending there was an 100%+ drop.
                  if (
                    !hasCompareData ||
                    lyNum === null ||
                    lyNum === undefined ||
                    lyNum === 0
                  ) {
                    return (
                      <div className="small text-muted">
                        {isLyComparison ? "vs LY: n/a" : "vs comp: n/a"}
                      </div>
                    );
                  }

                  // Round to 8 decimal places to eliminate floating-point noise
                  // (e.g. two 50.0% take rates differing by 4e-16 would show "0.01 pp")
                  const deltaAbs = Math.round(((curNum || 0) - (lyNum || 0)) * 1e8) / 1e8;
                  const deltaPct = lyNum !== 0 ? deltaAbs / (lyNum || 1) : null;
                  const positive = deltaAbs > 0;
                  const colorClass = positive
                    ? "text-success"
                    : deltaAbs < 0
                      ? "text-danger"
                      : "text-muted";
                  const arrow = deltaAbs > 0 ? "▲" : deltaAbs < 0 ? "▼" : "";

                  const prefix = (
                    <span className="text-muted">
                      {isLyComparison ? "vs LY:" : "vs comp:"}&nbsp;
                    </span>
                  );

                  if (opts.type === "currency") {
                    const absVal = Math.abs(deltaAbs);
                    return (
                      <div className="small">
                        {prefix}
                        <span className={colorClass}>
                          {arrow}{" "}
                          {deltaPct !== null
                            ? `${(deltaPct * 100).toFixed(1)}%`
                            : "—"}{" "}
                          {` (${deltaAbs >= 0 ? "+" : "-"}€${fmtCurrency(absVal)})`}
                        </span>
                      </div>
                    );
                  }

                  if (opts.type === "points") {
                    const points = (deltaAbs * 100).toFixed(2);
                    return (
                      <div className="small">
                        {prefix}
                        <span className={colorClass}>
                          {arrow} {points} pp{" "}
                          {deltaPct !== null
                            ? `(${(deltaPct * 100).toFixed(1)}%)`
                            : ""}
                        </span>
                      </div>
                    );
                  }

                  // default: count/percent
                  return (
                    <div className="small">
                      {prefix}
                      <span className={colorClass}>
                        {arrow}{" "}
                        {deltaPct !== null
                          ? `${(deltaPct * 100).toFixed(1)}%`
                          : "—"}{" "}
                        {` (${deltaAbs >= 0 ? "+" : ""}${deltaAbs})`}
                      </span>
                    </div>
                  );
                };

                const makeCard = (
                  cls,
                  icon,
                  label,
                  curValueFormatted,
                  curRaw,
                  lyRaw,
                  deltaOpts,
                ) => (
                  <div
                    className={`dashboard-kpi-card ${cls}`}
                    key={label}
                    style={{
                      minHeight: 96,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      padding: "10px 12px",
                    }}
                  >
                    {/* top: icon + title */}
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <i
                        className={`fas fa-${icon}`}
                        style={{ fontSize: 12, color: "#6b7280" }}
                      ></i>
                      <div
                        className="kpi-label"
                        style={{ margin: 0, fontSize: 12 }}
                      >
                        {label}
                      </div>
                    </div>

                    {/* middle: large value */}
                    <div style={{ marginTop: 8 }}>
                      <div
                        className="kpi-value"
                        style={{ fontSize: 20, fontWeight: 700 }}
                      >
                        {curValueFormatted}
                      </div>
                    </div>

                    {/* bottom: LY variation */}
                    <div style={{ marginTop: 8 }}>
                      {renderDelta(curRaw, lyRaw, deltaOpts)}
                    </div>
                  </div>
                );

                // Build displayed/current values depending on role (ADMIN sees complement)
                const bmFilterLocal = (this.state.businessModel || "").toString();
                const isRevenueShareFilter = bmFilterLocal === "revenue_share" || bmFilterLocal.toUpperCase() === "REVENUE SHARE";
                const currentDisplayed = { ...current };
                const lyDisplayed = { ...lyAgg };
                // compute GMV only for REVENUE SHARE arqueos (current period)
                const rsCurrentAggAll = this.aggregateFromRows(
                  (current.rows || []).filter(
                    (r) => ((r.BusinessModel || "").toString().toUpperCase() === "REVENUE SHARE"),
                  ),
                );
                const gmvRevenue = Number(rsCurrentAggAll.gmv || 0);

                // CommissionAmount stores the *customer's* kept share, calculated on GMV_net.
                // ADMIN needs to see Vacway's share = GMV_net_RS − CommissionAmount_RS.
                // The inversion must use GMV_net (not gross) as the base because
                // CommissionAmount is net-based — mixing gross GMV with a net commission
                // produces a wrong take rate (e.g. 58.7% instead of 50% with 21% VAT).
                // CUSTOMER role shows CommissionAmount directly (their own kept share).
                // Take rate for ADMIN is also net-based for consistency.
                // All GMV values use GMV_net throughout. CommissionAmount is also net-based.
                let currentTakeRateBase = currentDisplayed.gmv; // gross, used for non-ADMIN
                let lyTakeRateBase = lyDisplayed.gmv;
                if (roleLabel === "ADMIN") {
                  const rsRows = (current.rows || []).filter(
                    (r) => (r.BusinessModel || "").toString().toUpperCase() === "REVENUE SHARE",
                  );
                  const rsLyRows = (lyAgg.rows || []).filter(
                    (r) => (r.BusinessModel || "").toString().toUpperCase() === "REVENUE SHARE",
                  );
                  // Net GMV for RS rows — this is the base CommissionAmount was calculated on
                  const rsNetGmv = rsRows.reduce(
                    (s, r) => s + (Number(r.GMV_net || r.SalesTotal || 0) || 0), 0,
                  );
                  const rsLyNetGmv = rsLyRows.reduce(
                    (s, r) => s + (Number(r.GMV_net || r.SalesTotal || 0) || 0), 0,
                  );
                  const rsCommission = rsRows.reduce(
                    (s, r) => s + (Number(r.CommissionAmount || 0) || 0), 0,
                  );
                  const rsLyCommission = rsLyRows.reduce(
                    (s, r) => s + (Number(r.CommissionAmount || 0) || 0), 0,
                  );
                  // Invert: Vacway commission = net GMV_RS − customer kept amount
                  currentDisplayed.commission = rsNetGmv - rsCommission;
                  lyDisplayed.commission = rsLyNetGmv - rsLyCommission;
                  // Take rate denominator must match the commission base (net)
                  currentTakeRateBase = rsNetGmv;
                  lyTakeRateBase = rsLyNetGmv;
                }

                const currentTakeRate =
                  currentTakeRateBase > 0 ? currentDisplayed.commission / currentTakeRateBase : null;
                const lyTakeRate =
                  lyTakeRateBase > 0 ? lyDisplayed.commission / lyTakeRateBase : null;

                return [
                  makeCard(
                    "total",
                    "coins",
                    "GMV",
                    `${fmtCurrency(current.gmv)} €`,
                    current.gmv,
                    lyAgg.gmv,
                    { type: "currency" },
                  ),
                  makeCard(
                    "green-block",
                    "hand-holding-usd",
                    "Comisión neta",
                    `${fmtCurrency(Number(currentDisplayed.commission || 0))} €`,
                    Number(currentDisplayed.commission || 0),
                    lyDisplayed.commission,
                    { type: "currency" },
                  ),
                  makeCard(
                    "pinpad-active",
                    "percent",
                    "Take rate",
                    currentTakeRate !== null ? pct(currentTakeRate) : "—",
                    currentTakeRate,
                    lyTakeRate,
                    { type: "points" },
                  ),
                  makeCard(
                    "pinpad-block",
                    "receipt",
                    "Transacciones",
                    current.transactions,
                    current.transactions,
                    lyAgg.transactions,
                    { type: "count" },
                  ),
                  makeCard(
                    "red-block",
                    "ticket-alt",
                    "Ticket medio",
                    current.ticketAvg
                      ? `${fmtCurrency(current.ticketAvg)} €`
                      : "—",
                    current.ticketAvg,
                    lyAgg.ticketAvg,
                    { type: "currency" },
                  ),
                  makeCard(
                    "broken",
                    "chart-area",
                    "Ocupación ajustada",
                    op.avgOccFinal !== null ? pct(op.avgOccFinal) : "—",
                    op.avgOccFinal,
                    lyOpAgg.avgOccFinal,
                    { type: "points" },
                  ),
                  makeCard(
                    "door-block",
                    "tools",
                    "Disponibilidad",
                    op.avgAvailFinal !== null ? pct(op.avgAvailFinal) : "—",
                    op.avgAvailFinal,
                    lyOpAgg.avgAvailFinal,
                    { type: "points" },
                  ),
                  makeCard(
                    "pinpad-block",
                    "exclamation-circle",
                    "Incidencias",
                    op.incidences,
                    op.incidences,
                    lyOpAgg.incidences,
                    { type: "count" },
                  ),
                ];
              })()}
            </div>

            {/* Installed donuts — three separate cards (each: header + donut/list columns) */}
            <div className="dashboard-kpi-row mb-3">
              <div className="dashboard-card-section">
                <div className="card-body-custom">
                  {(() => {
                    const comp = hasCompareData
                      ? _byCountry_forInstalledCompare
                      : null;
                    return this.renderInstalledDonutCard(
                      "Instaladas por país",
                      _byCountry_forInstalled,
                      "country",
                      "globe",
                      _totalInstalledFiltered,
                      comp,
                    );
                  })()}
                </div>
              </div>
              <div className="dashboard-card-section">
                <div className="card-body-custom">
                  {(() => {
                    const comp = hasCompareData
                      ? _byCategory_forInstalledCompare
                      : null;
                    return this.renderInstalledDonutCard(
                      "Instaladas por vertical",
                      _byCategory_forInstalled,
                      "category",
                      "layer-group",
                      _totalInstalledFiltered,
                      comp,
                    );
                  })()}
                </div>
              </div>
              <div className="dashboard-card-section">
                <div className="card-body-custom">
                  {(() => {
                    const comp = hasCompareData
                      ? _byBusiness_forInstalledCompare
                      : null;
                    return this.renderInstalledDonutCard(
                      "Instaladas por modelo negocio",
                      _byBusiness_forInstalled,
                      "businessModel",
                      "briefcase",
                      _totalInstalledFiltered,
                      comp,
                    );
                  })()}
                </div>
              </div>
            </div>

            {/* Locker KPIs row (GMV per locker, Commission per locker, Trend) */}
            {this.renderLockerKPIs()}

            {/* top-five clients card wrapped in same grid/spacing as KPI row */}
            <div className="dashboard-kpi-row mb-3">
              <div
                className="dashboard-card-section mb-0"
                style={{ width: "100%" }}
              >
                <div className="card-header-custom">
                  <i className="fas fa-list-ol mr-2"></i>Top 5 clientes
                </div>
                <div className="card-body-custom">
                  {this.renderTopLocations()}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  generatedSalesArqueos: state.arqueos.generatedSalesArqueos,
  generatedOperationalArqueos: state.arqueos.generatedOperationalArqueos,
  error: state.arqueos.error,
  auth: state.auth,
  role: state.auth.role,
  customerId: state.auth.id,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      fetchPersistedSalesArqueos,
      fetchPersistedOperationalArqueos,
    },
    dispatch,
  );

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(OperationalDashboardView);
